import * as React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Skeleton,
  Drawer,
  Divider,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Popover,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import axios from "axios";
import { useAuth } from "../services/auth.service";
import XLSX from "xlsx";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";


const formatMoney = (value, currency = "USD") => {
  const num = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCleanPoliza = (polizaStr) => {
  if (!polizaStr) return "";
  return polizaStr.trim().split(/\s+/)[0];
};

const getVigenciaPoliza = (polizaStr) => {
  if (!polizaStr) return "";
  const parts = polizaStr.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(" ") : "";
};

const addWeeks = (dateStr, weeks) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().split("T")[0];
};

const statusChip = (status) => {
  const s = String(status || "").toUpperCase();
  const common = {
    size: "small",
    variant: "filled",
    sx: {
      height: 22,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      borderRadius: 999,
    },
  };
  if (s === "COMPLETED")
    return (
      <Chip
        {...common}
        label="Completado"
        sx={{ ...common.sx, bgcolor: "#fcdeb5", color: "#574425" }}
      />
    );
  if (s === "EXECUTED")
    return (
      <Chip
        {...common}
        label="Ejecutado"
        sx={{ ...common.sx, bgcolor: "#e8f0fe", color: "#1a73e8" }}
      />
    );
  if (s === "PENDING")
    return (
      <Chip
        {...common}
        label="Pendiente"
        sx={{ ...common.sx, bgcolor: "#d0e1fb", color: "#54647a" }}
      />
    );
  if (s === "FAILED")
    return (
      <Chip
        {...common}
        label="Fallido"
        sx={{ ...common.sx, bgcolor: "#ffdad6", color: "#93000a" }}
      />
    );
  return <Chip {...common} label={s === "COMPLETED" ? "Completado" : s === "EXECUTED" ? "Ejecutado" : s === "PENDING" ? "Pendiente" : s === "FAILED" ? "Fallido" : (s || "—")} sx={{ ...common.sx, bgcolor: "#eceef0", color: "#45464d" }} />;
};

const CREDITOR_PRESETS = [
  {
    alias: "segurosgenesis",
    type: "Directorio"
  },
  {
    alias: "Seguros Genesis",
    type: "Punto Yappy"
  },
  {
    alias: "SegurosGenesis E-COMMERCE",
    type: "Boton de Pago",
    details: [
      {
        id: "url",
        value: "http://us-central1-segenesis-cotizador.cloudfunctions.net"
      }
    ]
  },
  {
    alias: "GenesisSeguros",
    type: "Boton de Pago",
    details: [
      {
        id: "url",
        value: "https://www.facilseguro.com"
      }
    ]
  },
  {
    alias: "Boton cotizador",
    type: "Boton de Pago",
    details: [
      {
        id: "url",
        value: "https://insurance-apps.com"
      }
    ]
  },
  {
    alias: "prueba 2026",
    type: "Boton de Pago",
    details: [
      {
        id: "url",
        value: "https://www.segenesis.org"
      }
    ]
  }
];

const DEFAULT_TOKEN = process.env.REACT_APP_DEFAULT_TOKEN;

const maskAlias = (alias) => {
  if (!alias) return "—";

  return alias;
};

// Set DRY_RUN to true to test and view requests in console first without making real API calls.
// Set DRY_RUN to false to enable real network calls.
const DRY_RUN = false;

export default function TransaccionesYappy() {
  const { user, logout } = useAuth();
  const isAdmin = React.useMemo(() => {
    return user?.roles?.includes("ROLE_ADMIN") || false;
  }, [user]);

  const [transactions, setTransactions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showStats, setShowStats] = React.useState(false);
  const [statsData, setStatsData] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const [query, setQuery] = React.useState("");
  const [searchVal, setSearchVal] = React.useState("");
  const [status, setStatus] = React.useState("ALL");
  const [type, setType] = React.useState("ALL");
  const [role, setRole] = React.useState("ALL");
  const [category, setCategory] = React.useState("ALL");
  const [debitorAlias, setDebitorAlias] = React.useState("");
  const [debitorName, setDebitorName] = React.useState("");
  const [creditorAlias, setCreditorAlias] = React.useState("");
  const [showCustomCreditorInput, setShowCustomCreditorInput] = React.useState(false);
  const [creditorName, setCreditorName] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [appliedStartDate, setAppliedStartDate] = React.useState("");
  const [appliedEndDate, setAppliedEndDate] = React.useState("");
  const [enableDateRange, setEnableDateRange] = React.useState(false);
  const [isExternalMode, setIsExternalMode] = React.useState(false);
  const [dateField, setDateField] = React.useState("payment_date");
  const [sortBy, setSortBy] = React.useState("payment_date");
  const [sortOrder, setSortOrder] = React.useState("desc");

  // Single transaction details & drawer state
  const [selectedTxId, setSelectedTxId] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState(null);
  const [drawerLoading, setDrawerLoading] = React.useState(false);
  const [drawerError, setDrawerError] = React.useState(null);
  const [currentlyUpdatingTxId, setCurrentlyUpdatingTxId] = React.useState(null);
  const processedExecutedTxIdsRef = React.useRef(new Set());

  // Manual search/lookup input
  const [lookupId, setLookupId] = React.useState("");

  // PowerBI API search modal states
  const [searchModalOpen, setSearchModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchTab, setSearchTab] = React.useState("cedula"); // "cedula", "subgrupo", or "nombre"
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState(null);
  const [searchResults, setSearchResults] = React.useState(null);
  const [cedulaActiveSubTab, setCedulaActiveSubTab] = React.useState(0); // 0 = Personas, 1 = Pólizas, 2 = Subgrupos

  // FacilSeguro integration states
  const [selectedAsegurado, setSelectedAsegurado] = React.useState(null);
  const [lastSelectedAsegurado, setLastSelectedAsegurado] = React.useState(null);

  React.useEffect(() => {
    if (selectedAsegurado) {
      setLastSelectedAsegurado(selectedAsegurado);
    }
  }, [selectedAsegurado]);

  const [facilseguroDetails, setFacilseguroDetails] = React.useState(null);
  const [facilseguroLoading, setFacilseguroLoading] = React.useState(false);
  const [facilseguroError, setFacilseguroError] = React.useState(null);
  const [detailPolicy, setDetailPolicy] = React.useState(null);

  // Abonos hacia Pólizas states
  const [abonosMap, setAbonosMap] = React.useState({}); // { [txId]: [ { id, polizaNo, asegurado, compania, abonoAmount } ] }
  const [selectedPolicyForAbono, setSelectedPolicyForAbono] = React.useState(null);
  const [abonoInputAmount, setAbonoInputAmount] = React.useState("");
  const [abonoInputDate, setAbonoInputDate] = React.useState("");

  React.useEffect(() => {
    if (selectedPolicyForAbono) {
      const txDateStr = drawerData?.payment_date || drawerData?.registration_date || drawerData?.createdAt;
      if (txDateStr) {
        const txDate = new Date(txDateStr);
        const yyyy = txDate.getFullYear();
        const mm = String(txDate.getMonth() + 1).padStart(2, '0');
        const dd = String(txDate.getDate()).padStart(2, '0');
        setAbonoInputDate(`${yyyy}-${mm}-${dd}`);
      } else {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        setAbonoInputDate(`${yyyy}-${mm}-${dd}`);
      }
    } else {
      setAbonoInputDate("");
    }
  }, [selectedPolicyForAbono, drawerData]);

  // Editing & Applying states for abonos
  const [editingAbonoId, setEditingAbonoId] = React.useState(null);
  const [editingAbonoAmount, setEditingAbonoAmount] = React.useState("");
  const [abonoConfirmOpen, setAbonoConfirmOpen] = React.useState(false);
  const [abonoToConfirm, setAbonoToConfirm] = React.useState(null);
  const [applyingAbonoId, setApplyingAbonoId] = React.useState(null);

  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState(null);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const currentAbonos = React.useMemo(() => {
    return selectedTxId ? (abonosMap[selectedTxId] || []) : [];
  }, [abonosMap, selectedTxId]);

  const currentAbonosSum = React.useMemo(() => {
    const rawSum = currentAbonos.reduce((sum, item) => sum + item.abonoAmount, 0);
    return Math.round(rawSum * 100) / 100;
  }, [currentAbonos]);

  const txTotalAmount = React.useMemo(() => {
    return drawerData ? Number(drawerData.charge?.amount || 0) : 0;
  }, [drawerData]);

  const remainingTxAmount = React.useMemo(() => {
    const rawRemaining = txTotalAmount - currentAbonosSum;
    return Math.max(0, Math.round(rawRemaining * 100) / 100);
  }, [txTotalAmount, currentAbonosSum]);

  const cannotModifyAbonos = React.useMemo(() => {
    const txDateStr = drawerData?.payment_date || drawerData?.registration_date || drawerData?.createdAt;
    if (!txDateStr) return false;
    const isBeforeJun2026 = new Date(txDateStr).getTime() < new Date("2026-06-01T00:00:00Z").getTime();
    return !isAdmin && isBeforeJun2026;
  }, [drawerData, isAdmin]);

  const abonoError = React.useMemo(() => {
    if (!abonoInputAmount) return null;
    const amount = Number(abonoInputAmount);
    if (isNaN(amount) || amount <= 0) {
      return "Ingrese un monto mayor a 0.";
    }
    if (amount > remainingTxAmount) {
      return `No puede exceder el restante (${formatMoney(remainingTxAmount)}).`;
    }
    return null;
  }, [abonoInputAmount, remainingTxAmount]);

  const executeSearch = React.useCallback(async (tab, queryVal) => {
    if (!queryVal || !queryVal.trim()) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults(null);
    setSelectedAsegurado(null);
    setFacilseguroDetails(null);
    setFacilseguroLoading(false);
    setFacilseguroError(null);
    setDetailPolicy(null);

    try {
      const url = tab === "cedula"
        ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/cedula?q=${encodeURIComponent(queryVal.trim())}`
        : tab === "telefono"
          ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/celular?q=${encodeURIComponent(queryVal.trim())}`
          : tab === "nombre"
            ? `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/asegurado?q=${encodeURIComponent(queryVal.trim())}`
            : `${process.env.REACT_APP_POWERBIAPI_URL}/api/ext/subgrupo?q=${encodeURIComponent(queryVal.trim())}`;

      const headers = {
        "accept": "*/*",
        "Authorization": `Bearer ${process.env.REACT_APP_POWERBIAPI_TOKEN}`
      };

      const response = await axios.get(url, { headers });
      setSearchResults(response.data);
    } catch (err) {
      console.error("Error executing PowerBI API query:", err);
      setSearchError(
        err.response?.data?.message || "Ocurrió un error al consultar la API externa."
      );
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const exportToExcel = () => {
    const rows = filtered.map(t => {
      const preset = t.creditor?.alias ? CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === t.creditor.alias.trim().toLowerCase()) : null;
      const isDirectorio = preset ? preset.type === "Directorio" : false;

      let hasAbonos = false;
      let remainingAmount = 0;

      if (isDirectorio) {
        const abonosList = t.abonos || abonosMap[t.id] || [];
        const abonosSum = Math.round(abonosList.reduce((sum, item) => sum + (item.abonoAmount || 0), 0) * 100) / 100;
        remainingAmount = Math.max(0, Math.round(((t.charge?.amount || 0) - abonosSum) * 100) / 100);
        hasAbonos = abonosList.length > 0;
      }

      return {
        "ID Transacción": t.id || "—",
        "Fecha": formatDateTime(t.payment_date || t.registration_date || t.createdAt),
        "Emisor (Nombre)": t.debitor?.complete_name || "—",
        "Emisor (Alias)": t.debitor?.alias || "—",
        "Receptor (Nombre)": t.creditor?.complete_name || "—",
        "Receptor (Alias)": t.creditor?.alias || "—",
        "Abonado": !isDirectorio ? "Abonado" : (hasAbonos ? "Abonado" : "Sin Abonar"),
        "Saldo Restante ($)": remainingAmount,
        "Monto ($)": t.charge?.amount || 0,
        "Comisión ($)": t.fee?.amount || 0,
        "Estado": t.status === "COMPLETED" ? "Completado" : t.status === "PENDING" ? "Pendiente" : t.status === "FAILED" ? "Fallido" : (t.status || "—"),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");

    // Auto-fit columns
    const maxProps = Object.keys(rows[0] || {});
    const wscols = maxProps.map(key => {
      const maxLen = Math.max(
        key.length,
        ...rows.map(row => String(row[key] ?? "").length)
      );
      return { wch: maxLen + 3 };
    });
    worksheet["!cols"] = wscols;

    XLSX.writeFile(workbook, `Reporte_Yappy_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleOpenSearchModal = React.useCallback((tabType = "cedula", initialQuery = "") => {
    setSearchTab(tabType);
    setSearchQuery(initialQuery);
    setSearchResults(null);
    setSearchError(null);
    setCedulaActiveSubTab(0);
    setSelectedPolicyForAbono(null);
    setAbonoInputAmount("");
    setSelectedAsegurado(null);
    setFacilseguroDetails(null);
    setFacilseguroLoading(false);
    setFacilseguroError(null);
    setDetailPolicy(null);
    setSearchModalOpen(true);

    if (initialQuery && initialQuery.trim()) {
      executeSearch(tabType, initialQuery);
    }
  }, [executeSearch]);

  const handleCloseSearchModal = React.useCallback(() => {
    setSearchModalOpen(false);
    setSelectedPolicyForAbono(null);
    setAbonoInputAmount("");
    setSelectedAsegurado(null);
    setFacilseguroDetails(null);
    setFacilseguroLoading(false);
    setFacilseguroError(null);
    setDetailPolicy(null);
  }, []);

  const handleSearchExecute = () => {
    executeSearch(searchTab, searchQuery);
  };

  const getUniqueAsegurados = React.useCallback((results) => {
    if (!results) return [];
    const map = new Map();

    const getIdEntidad = (item) => {
      return item?.IdEntidad ?? item?.idEntidad ?? item?.IdPersona ?? item?.idPersona ?? item?.IDENTIDAD ?? item?.identidad ?? item?.IdAsegurado;
    };

    const getAseguradoName = (item) => {
      return item?.Nombre ?? item?.Asegurado ?? item?.complete_name ?? item?.NombreCompleto;
    };

    const getAseguradoIdentificacion = (item) => {
      return item?.Cedula ?? item?.Ruc ?? item?.Identificacion ?? item?.Identificación ?? item?.cedula;
    };

    // 1. Process results.personas
    if (Array.isArray(results.personas)) {
      results.personas.forEach(p => {
        const id = getIdEntidad(p);
        if (id && !map.has(id)) {
          map.set(id, {
            idEntidad: id,
            nombre: getAseguradoName(p),
            identificacion: getAseguradoIdentificacion(p),
            celular: p.Celular || p.TelefonoMovil || "",
            telefono: p.Telefono || p.TelefonoCasa || "",
            email: p.Email || p.Correo || "",
            source: 'persona',
            raw: p
          });
        }
      });
    }

    // 2. Process results.polizas
    if (Array.isArray(results.polizas)) {
      results.polizas.forEach(p => {
        const id = getIdEntidad(p);
        if (id && !map.has(id)) {
          map.set(id, {
            idEntidad: id,
            nombre: getAseguradoName(p),
            identificacion: getAseguradoIdentificacion(p),
            celular: p.Celular || p.TelefonoMovil || "",
            telefono: p.Telefono || p.TelefonoCasa || "",
            email: p.Email || p.Correo || "",
            source: 'poliza',
            raw: p
          });
        }
      });
    }

    return Array.from(map.values());
  }, []);

  const handleSelectAsegurado = React.useCallback(async (asegurado) => {
    if (!asegurado || !asegurado.idEntidad) return;

    setSelectedAsegurado(asegurado);
    setFacilseguroLoading(true);
    setFacilseguroError(null);
    setFacilseguroDetails(null);
    setSelectedPolicyForAbono(null);
    setAbonoInputAmount("");

    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api";
      const url = `${apiBase}/facilseguro/polizas/${asegurado.idEntidad}`;

      const token = user?.accessToken || DEFAULT_TOKEN;
      const headers = {
        "accept": "*/*",
        "x-access-token": token,
        "Authorization": `Bearer ${token}`
      };

      const response = await axios.get(url, { headers });
      const data = response.data;
      if (data && Array.isArray(data.Polizas)) {
        const insuredName = data.Asegurado?.[0]?.Asegurado || asegurado.nombre || "—";
        data.Polizas = data.Polizas.map(p => {
          // Format NroPoliza to contain the dates so that getCleanPoliza and getVigenciaPoliza work out-of-the-box
          const datesStr = (p.FechaDesde && p.FechaHasta) ? ` ${p.FechaDesde} a ${p.FechaHasta}` : "";
          const newNroPoliza = `${p.NroPoliza || ""}${datesStr}`.trim();

          return {
            ...p,
            NroPoliza: newNroPoliza,
            Asegurado: insuredName,
            PrimaNeta: p.Prima ?? p.PrimaSinImpuesto ?? 0
          };
        });
      }
      setFacilseguroDetails(data);
    } catch (err) {
      console.error("Error fetching facilseguro polizas:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
        return;
      }
      setFacilseguroError(
        err.response?.data?.message || "Ocurrió un error al consultar las pólizas del asegurado."
      );
    } finally {
      setFacilseguroLoading(false);
    }
  }, [user, DEFAULT_TOKEN]);

  const [hasNextPage, setHasNextPage] = React.useState(false);
  const pageTokensRef = React.useRef({ 1: null });

  const resetPagination = React.useCallback(() => {
    setPage(1);
    pageTokensRef.current = { 1: null };
  }, []);

  const isExternal = React.useMemo(() => !!(isExternalMode && appliedStartDate && appliedEndDate), [isExternalMode, appliedStartDate, appliedEndDate]);

  const fetchTransactions = React.useCallback(async () => {
    processedExecutedTxIdsRef.current.clear();
    setLoading(true);
    setError(null);
    try {
      const headers = {
        "Content-Type": "application/json",
        "accept": "application/json",
      };
      const token = user?.accessToken || DEFAULT_TOKEN;
      headers["x-access-token"] = token;
      headers["Authorization"] = `Bearer ${token}`;

      if (isExternal) {
        const apiBase = process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api";
        const url = `${apiBase}/yappy/external-transactions`;
        const currentToken = pageTokensRef.current[page] || null;

        const reqData = {
          limit: limit,
          start_date: appliedStartDate,
          end_date: appliedEndDate,
        };
        if (currentToken) {
          reqData.token = currentToken;
        }

        const response = await axios.post(url, reqData, { headers });

        if (response.data && response.data.success) {
          const body = response.data.data?.body || {};
          const txs = body.transactions || [];
          const pagination = body.pagination || {};
          const nextToken = pagination.token;
          const hasNext = pagination.has_next_page || false;

          setTransactions(txs);
          setHasNextPage(hasNext);
          if (hasNext && nextToken) {
            pageTokensRef.current = {
              ...pageTokensRef.current,
              [page + 1]: nextToken,
            };
          }
          setTotalPages(hasNext ? page + 1 : page);
        } else {
          setError("Error al cargar las transacciones externas");
        }
      } else {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);

        if (query.trim()) params.append("search", query.trim());
        if (status !== "ALL") params.append("status", status);
        if (type !== "ALL") params.append("type", type);
        if (role !== "ALL") params.append("role", role);
        if (category !== "ALL") params.append("category", category);
        if (debitorAlias.trim()) params.append("debitorAlias", debitorAlias.trim());
        if (debitorName.trim()) params.append("debitorName", debitorName.trim());
        if (creditorAlias.trim()) params.append("creditorAlias", creditorAlias.trim());
        if (creditorName.trim()) params.append("creditorName", creditorName.trim());
        if (appliedStartDate) params.append("startDate", appliedStartDate);
        if (appliedEndDate) params.append("endDate", appliedEndDate);
        if (showStats && isAdmin) params.append("stats", "true");
        params.append("dateField", dateField);
        params.append("sortBy", sortBy);
        params.append("sortOrder", sortOrder);

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API}/yappy/transactions?${params.toString()}`,
          { headers }
        );

        if (response.data && response.data.success) {
          setTransactions(response.data.transactions || []);
          setTotal(response.data.total || 0);
          setTotalPages(response.data.totalPages || 0);
          if (response.data.stats) {
            setStatsData(response.data.stats);
          } else {
            setStatsData(null);
          }
        } else {
          setError("Error al cargar las transacciones");
        }
      }
    } catch (err) {
      console.error("Error fetching Yappy transactions:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
        return;
      }
      setError(
        err.response?.data?.message ||
        "Error de red al obtener las transacciones de Yappy"
      );
    } finally {
      setLoading(false);
    }
  }, [
    user?.accessToken,
    page,
    limit,
    query,
    status,
    type,
    role,
    category,
    debitorAlias,
    debitorName,
    creditorAlias,
    creditorName,
    appliedStartDate,
    appliedEndDate,
    enableDateRange,
    dateField,
    sortBy,
    sortOrder,
    isExternal,
    showStats,
    isAdmin,
  ]);

  React.useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const updateTransactionInState = React.useCallback((updatedTx) => {
    if (!updatedTx || !updatedTx.id) return;
    setTransactions((prev) =>
      prev.map((t) => (t.id === updatedTx.id ? { ...t, ...updatedTx } : t))
    );
    setDrawerData((prev) =>
      prev && prev.id === updatedTx.id ? { ...prev, ...updatedTx } : prev
    );
    if (updatedTx.abonos) {
      setAbonosMap((prev) => ({
        ...prev,
        [updatedTx.id]: updatedTx.abonos,
      }));
    }
  }, []);

  React.useEffect(() => {
    if (!selectedTxId) return;

    const fetchSingleTx = async () => {
      setDrawerLoading(true);
      setDrawerError(null);
      setDrawerData(null);
      try {
        const headers = {
          "Content-Type": "application/json",
        };
        if (user?.accessToken) {
          headers["x-access-token"] = user.accessToken;
          headers["Authorization"] = `Bearer ${user.accessToken}`;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API}/yappy/movement/${selectedTxId}`,
          { headers }
        );

        if (response.data && response.data.success) {
          const tx = response.data.transaction;
          setDrawerData(tx);
          if (tx) {
            updateTransactionInState(tx);
          }
        } else {
          setDrawerError("No se pudo obtener el detalle de la transacción");
        }
      } catch (err) {
        console.error("Error fetching single transaction:", err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          logout();
          return;
        }
        setDrawerError(
          err.response?.data?.message ||
          "Error de red al obtener la transacción"
        );
      } finally {
        setDrawerLoading(false);
      }
    };

    fetchSingleTx();
  }, [selectedTxId, user?.accessToken, updateTransactionInState]);

  const dateRangeString = React.useMemo(() => {
    if (!transactions || transactions.length === 0) return "Sin transacciones";
    const dates = transactions
      .map(t => t.payment_date || t.registration_date)
      .filter(Boolean)
      .map(d => new Date(d))
      .filter(d => !isNaN(d.getTime()));
    if (dates.length === 0) return "Sin rango de fecha";

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    const format = (d) => d.toLocaleDateString("es-PA", { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${format(minDate)} - ${format(maxDate)}`;
  }, [transactions]);

  const filtered = React.useMemo(() => {
    if (!isExternal) return transactions;

    let result = [...transactions];

    // Client-side filtering
    result = result.filter((t) => {
      if (status !== "ALL" && String(t.status || "").toUpperCase() !== status) return false;
      if (type !== "ALL" && t.type !== type) return false;
      if (role !== "ALL" && t.role !== role) return false;
      if (category !== "ALL" && t.category !== category) return false;

      if (debitorName.trim() && !String(t.debitor?.complete_name || "").toLowerCase().includes(debitorName.trim().toLowerCase())) return false;
      if (debitorAlias.trim() && !String(t.debitor?.alias || "").toLowerCase().includes(debitorAlias.trim().toLowerCase())) return false;

      if (creditorName.trim() && !String(t.creditor?.complete_name || "").toLowerCase().includes(creditorName.trim().toLowerCase())) return false;
      if (creditorAlias.trim() && !String(t.creditor?.alias || "").toLowerCase().includes(creditorAlias.trim().toLowerCase())) return false;

      if (query.trim()) {
        const q = query.trim().toLowerCase();
        const matchesQuery =
          String(t.id || "").toLowerCase().includes(q) ||
          String(t.debitor?.complete_name || "").toLowerCase().includes(q) ||
          String(t.debitor?.alias || "").toLowerCase().includes(q) ||
          String(t.creditor?.complete_name || "").toLowerCase().includes(q) ||
          String(t.creditor?.alias || "").toLowerCase().includes(q);
        if (!matchesQuery) return false;
      }
      return true;
    });

    // Client-side sorting
    result.sort((a, b) => {
      let valA, valB;
      if (sortBy === "payment_date") {
        valA = a.payment_date ? new Date(a.payment_date).getTime() : 0;
        valB = b.payment_date ? new Date(b.payment_date).getTime() : 0;
      } else if (sortBy === "createdAt") {
        valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (sortBy === "registration_date") {
        valA = a.registration_date ? new Date(a.registration_date).getTime() : 0;
        valB = b.registration_date ? new Date(b.registration_date).getTime() : 0;
      } else if (sortBy === "charge.amount") {
        valA = Number(a.charge?.amount ?? 0);
        valB = Number(b.charge?.amount ?? 0);
      } else {
        valA = a[sortBy];
        valB = b[sortBy];
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [
    transactions,
    isExternal,
    status,
    type,
    role,
    category,
    debitorName,
    debitorAlias,
    creditorName,
    creditorAlias,
    query,
    sortBy,
    sortOrder
  ]);

  const totals = React.useMemo(() => {
    const volume = filtered.reduce((acc, t) => acc + Number(t.charge?.amount ?? 0), 0);
    const fees = filtered.reduce((acc, t) => acc + Number(t.fee?.amount ?? 0), 0);

    return { volume, fees, count: filtered.length };
  }, [filtered]);

  React.useEffect(() => {
    if (loading) return;

    const executedTxs = filtered.filter(
      (t) => String(t.status || "").toUpperCase() === "EXECUTED" && !processedExecutedTxIdsRef.current.has(t.id)
    );

    if (executedTxs.length === 0) return;

    let isCancelled = false;

    const pollExecuted = async () => {
      for (const tx of executedTxs) {
        if (isCancelled) break;

        processedExecutedTxIdsRef.current.add(tx.id);
        setCurrentlyUpdatingTxId(tx.id);
        try {
          const headers = {
            "Content-Type": "application/json",
          };
          const token = user?.accessToken || DEFAULT_TOKEN;
          if (token) {
            headers["x-access-token"] = token;
            headers["Authorization"] = `Bearer ${token}`;
          }

          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_API}/yappy/movement/${tx.id}`,
            { headers }
          );

          if (!isCancelled && response.data && response.data.success && response.data.transaction) {
            updateTransactionInState(response.data.transaction);
          }
        } catch (err) {
          console.error(`Error querying executed transaction ${tx.id}:`, err);
        } finally {
          if (!isCancelled) {
            setCurrentlyUpdatingTxId(null);
          }
        }

        if (isCancelled) break;
        await new Promise((res) => setTimeout(res, 6000));
      }
    };

    pollExecuted();

    return () => {
      isCancelled = true;
      setCurrentlyUpdatingTxId(null);
    };
  }, [filtered, loading, user?.accessToken, updateTransactionInState]);

  const Card = ({ label, icon, value, sub }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: "#ffffff",
        border: "1px solid rgba(198,198,205,0.15)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 112,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ color: "#45464d", fontSize: 11, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Box sx={{ color: "#79849a", fontSize: 18, lineHeight: 1 }}>{icon}</Box>
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.6, color: "#191c1e" }}>
          {value}
        </Typography>
        {sub ? (
          <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 800, color: "#574425" }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: "#f7f9fb", minHeight: "calc(100vh - 64px)" }}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: "auto" }}>
        <Stack spacing={3.5}>
          {/* Page header */}
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 900, letterSpacing: -0.8, color: "#191c1e" }}>
                Transacciones Yappy
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 600, color: "#45464d" }}>
                Monitorizando y visualizando transacciones registradas de Yappy vía webhook en tiempo real.
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={fetchTransactions}
              sx={{
                bgcolor: "#000000",
                color: "#ffffff",
                px: 2.5,
                py: 1.2,
                borderRadius: 3,
                fontWeight: 900,
                textTransform: "none",
                "&:hover": { bgcolor: "#111c2d" },
              }}
              startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>}
            >
              Actualizar
            </Button>
          </Box>

          {/* Summary bento */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card
                label="Volumen de la Página"
                icon={<span className="material-symbols-outlined">payments</span>}
                value={formatMoney(totals.volume, "USD")}
                sub="Suma total de la página actual"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                label="Comisiones de la Página"
                icon={<span className="material-symbols-outlined">account_balance_wallet</span>}
                value={formatMoney(totals.fees, "USD")}
                sub={`Promedio: ${formatMoney(totals.count ? (totals.fees / totals.count) : 0, "USD")} por txn`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                label="Transacciones Activas"
                icon={<span className="material-symbols-outlined">sync</span>}
                value={String(totals.count)}
                sub={isExternal ? `Mostrando ${totals.count} de esta página` : `Mostrando ${totals.count} de ${total} transacciones en total`}
              />
            </Grid>
          </Grid>

          {/* Filtros avanzados a ancho completo */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid rgba(198,198,205,0.15)",
              boxShadow: "0 10px 30px rgba(25, 28, 30, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1.5, mb: 2.5, borderBottom: "1px solid rgba(198,198,205,0.10)" }}>
              <span className="material-symbols-outlined" style={{ color: "#191c1e" }}>
                tune
              </span>
              <Typography sx={{ fontSize: 16, fontWeight: 900, letterSpacing: -0.2 }}>
                Filtros del Libro de Transacciones
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Horizonte de Tiempo
                </Typography>
                <TextField
                  value={startDate && endDate ? `${dayjs(startDate).format("DD/MM/YYYY")} - ${dayjs(endDate).format("DD/MM/YYYY")}` : dateRangeString}
                  size="small"
                  fullWidth
                  onClick={(e) => setDatePickerAnchorEl(e.currentTarget)}
                  placeholder="Seleccionar rango de fechas"
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                          calendar_today
                        </span>
                      </InputAdornment>
                    ),
                    endAdornment: (enableDateRange || startDate || endDate) ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartDate("");
                            setEndDate("");
                            setAppliedStartDate("");
                            setAppliedEndDate("");
                            setEnableDateRange(false);
                            resetPagination();
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#79849a" }}>
                            close
                          </span>
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      cursor: "pointer",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    },
                    "& input": { cursor: "pointer" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Elementos por Página
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      resetPagination();
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value={10}>10 transacciones</MenuItem>
                    <MenuItem value={25}>25 transacciones</MenuItem>
                    <MenuItem value={50}>50 transacciones</MenuItem>
                    <MenuItem value={100}>100 transacciones</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Estado de Flujo
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      resetPagination();
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Estados</MenuItem>
                    <MenuItem value="COMPLETED">Completado</MenuItem>
                    <MenuItem value="PENDING">Pendiente</MenuItem>
                    <MenuItem value="FAILED">Fallido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Tipo de Transacción
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value);
                      resetPagination();
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Tipos</MenuItem>
                    <MenuItem value="TXN-COM">Comercio (TXN-COM)</MenuItem>
                    <MenuItem value="TXN-ECOM">E-Commerce (TXN-ECOM)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Rol de Transacción
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      resetPagination();
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Roles</MenuItem>
                    <MenuItem value="CREDIT">Crédito</MenuItem>
                    <MenuItem value="DEBIT">Débito</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Categoría
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      resetPagination();
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todas las Categorías</MenuItem>
                    <MenuItem value="INTRABANK">Intrabancaria (INTRABANK)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Ordenación
                </Typography>
                <Stack spacing={1}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{
                        bgcolor: "#f7f9fb",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#000000" },
                      }}
                    >
                      <MenuItem value="payment_date">Fecha de Pago</MenuItem>
                      <MenuItem value="createdAt">Creado en DB</MenuItem>
                      <MenuItem value="registration_date">Fecha de Registro</MenuItem>
                      <MenuItem value="charge.amount">Monto del Pago</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Sentido de Orden
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="desc">Descendente</MenuItem>
                    <MenuItem value="asc">Ascendente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Filtro de Emisor (Debitor)
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Nombre"
                    value={debitorName}
                    onChange={(e) => setDebitorName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                  <TextField
                    label="Alias / Teléfono"
                    value={debitorAlias}
                    onChange={(e) => setDebitorAlias(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Filtro de Receptor (Creditor)
                </Typography>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <TextField
                    label="Nombre"
                    value={creditorName}
                    onChange={(e) => setCreditorName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                  {showCustomCreditorInput ? (
                    <TextField
                      label="Alias / Teléfono"
                      value={creditorAlias}
                      onChange={(e) => setCreditorAlias(e.target.value)}
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setShowCustomCreditorInput(false);
                                setCreditorAlias("");
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>list</span>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <Select
                        value={creditorAlias}
                        onChange={(e) => {
                          if (e.target.value === "__custom__") {
                            setShowCustomCreditorInput(true);
                            setCreditorAlias("");
                          } else {
                            setCreditorAlias(e.target.value);
                          }
                        }}
                        displayEmpty
                        sx={{
                          bgcolor: "#f7f9fb",
                          borderRadius: 2,
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                          "&.Mui-focused fieldset": { borderColor: "#000000" },
                        }}
                      >
                        <MenuItem value="">Todos los Aliases</MenuItem>
                        {CREDITOR_PRESETS.map((preset) => (
                          <MenuItem key={preset.alias} value={preset.alias}>
                            {preset.alias} ({preset.type})
                          </MenuItem>
                        ))}
                        <MenuItem value="__custom__">
                          <em>Otro alias...</em>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", justifyContent: isAdmin ? "space-between" : "flex-end", alignItems: "center", gap: 1.5, mt: 1 }}>
                {isAdmin && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showStats}
                        onChange={(e) => {
                          setShowStats(e.target.checked);
                          if (!e.target.checked) {
                            setStatsData(null);
                          }
                        }}
                        color="default"
                        sx={{
                          color: "#000000",
                          '&.Mui-checked': {
                            color: "#000000",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#191c1e" }}>
                        Ver Estadísticas Globales (Admin)
                      </Typography>
                    }
                  />
                )}
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="text"
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      fontWeight: 900,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      fontSize: 10,
                      color: "#45464d",
                      "&:hover": { bgcolor: "#eceef0" },
                    }}
                    onClick={() => {
                      setQuery("");
                      setSearchVal("");
                      setStatus("ALL");
                      setType("ALL");
                      setRole("ALL");
                      setCategory("ALL");
                      setDebitorAlias("");
                      setDebitorName("");
                      setCreditorAlias("");
                      setShowCustomCreditorInput(false);
                      setCreditorName("");
                      setIsExternalMode(false);
                      setStartDate("");
                      setEndDate("");
                      setAppliedStartDate("");
                      setAppliedEndDate("");
                      setEnableDateRange(false);
                      setDateField("payment_date");
                      setSortBy("payment_date");
                      setSortOrder("desc");
                      setShowStats(false);
                      setStatsData(null);
                      resetPagination();
                    }}
                  >
                    Restablecer Filtros
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setAppliedStartDate(startDate);
                      setAppliedEndDate(endDate);
                      setIsExternalMode(false);
                      fetchTransactions();
                    }}
                    sx={{
                      bgcolor: "#000000",
                      color: "#ffffff",
                      borderRadius: 3,
                      px: 4,
                      py: 1,
                      fontWeight: 900,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      fontSize: 11,
                      "&:hover": { bgcolor: "#111c2d" },
                    }}
                  >
                    Actualizar Datos
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Datos Externos de Yappy */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid rgba(198,198,205,0.15)",
              boxShadow: "0 10px 30px rgba(25, 28, 30, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5, mb: 2.5, borderBottom: "1px solid rgba(198,198,205,0.10)", flexWrap: "wrap", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span className="material-symbols-outlined" style={{ color: "#191c1e" }}>
                  sync_alt
                </span>
                <Typography sx={{ fontSize: 16, fontWeight: 900, letterSpacing: -0.2 }}>
                  Datos Externos de Yappy
                </Typography>
              </Box>
              {enableDateRange && (
                <Chip
                  label="Consulta de API Externa Activa"
                  size="small"
                  sx={{
                    bgcolor: "#e6f4ea",
                    color: "#137333",
                    fontWeight: 900,
                    borderRadius: 999,
                    fontSize: 11,
                    border: "1px solid rgba(19, 115, 51, 0.15)"
                  }}
                />
              )}
            </Box>

            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Campo de Fecha
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="payment_date">Pago</MenuItem>
                    <MenuItem value="createdAt">DB</MenuItem>
                    <MenuItem value="registration_date">Registro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={8} md={6}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Rango de Fechas (Máx. 4 Semanas)
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={startDate && endDate ? `${dayjs(startDate).format("DD/MM/YYYY")} - ${dayjs(endDate).format("DD/MM/YYYY")}` : ""}
                  placeholder="Seleccionar rango de fechas"
                  onClick={(e) => setDatePickerAnchorEl(e.currentTarget)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "#79849a", cursor: "pointer" }}>
                          calendar_today
                        </span>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      cursor: "pointer"
                    },
                    "& input": { cursor: "pointer" }
                  }}
                />

                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      p: 2.5,
                      mt: 1,
                      borderRadius: 3,
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                      border: "1px solid rgba(0, 0, 0, 0.06)",
                      bgcolor: "#ffffff"
                    }
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2} sx={{ width: 340 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8, color: "#79849a" }}>
                        Seleccionar Rango de Fechas
                      </Typography>

                      <Stack direction="row" spacing={2}>
                        <DatePicker
                          label="Desde"
                          value={startDate ? dayjs(startDate) : null}
                          onChange={(newVal) => {
                            if (newVal) {
                              const formatted = newVal.format("YYYY-MM-DD");
                              setStartDate(formatted);
                              setEnableDateRange(true);
                              if (!endDate || dayjs(endDate).isBefore(dayjs(formatted))) {
                                setEndDate(addWeeks(formatted, 4));
                              }
                            } else {
                              setStartDate("");
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true
                            }
                          }}
                        />
                        <DatePicker
                          label="Hasta"
                          value={endDate ? dayjs(endDate) : null}
                          onChange={(newVal) => {
                            if (newVal) {
                              const formatted = newVal.format("YYYY-MM-DD");
                              setEndDate(formatted);
                              setEnableDateRange(true);
                              if (!startDate || dayjs(startDate).isAfter(dayjs(formatted))) {
                                setStartDate(addWeeks(formatted, -4));
                              }
                            } else {
                              setEndDate("");
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true
                            }
                          }}
                        />
                      </Stack>

                      {/* Quick filters */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        <Chip
                          label="Hoy"
                          size="small"
                          onClick={() => {
                            const today = dayjs().format("YYYY-MM-DD");
                            setStartDate(today);
                            setEndDate(today);
                            setEnableDateRange(true);
                            setAppliedStartDate(today);
                            setAppliedEndDate(today);
                            setDatePickerAnchorEl(null);
                            resetPagination();
                          }}
                          sx={{ fontWeight: 700, cursor: "pointer" }}
                        />
                        <Chip
                          label="Ayer"
                          size="small"
                          onClick={() => {
                            const yesterday = dayjs().subtract(1, 'day').format("YYYY-MM-DD");
                            setStartDate(yesterday);
                            setEndDate(yesterday);
                            setEnableDateRange(true);
                            setAppliedStartDate(yesterday);
                            setAppliedEndDate(yesterday);
                            setDatePickerAnchorEl(null);
                            resetPagination();
                          }}
                          sx={{ fontWeight: 700, cursor: "pointer" }}
                        />
                        <Chip
                          label="Últimos 7 días"
                          size="small"
                          onClick={() => {
                            const today = dayjs().format("YYYY-MM-DD");
                            const start = dayjs().subtract(6, 'day').format("YYYY-MM-DD");
                            setStartDate(start);
                            setEndDate(today);
                            setEnableDateRange(true);
                            setAppliedStartDate(start);
                            setAppliedEndDate(today);
                            setDatePickerAnchorEl(null);
                            resetPagination();
                          }}
                          sx={{ fontWeight: 700, cursor: "pointer" }}
                        />
                        <Chip
                          label="Últimas 4 semanas"
                          size="small"
                          onClick={() => {
                            const today = dayjs().format("YYYY-MM-DD");
                            const start = dayjs().subtract(4, 'weeks').format("YYYY-MM-DD");
                            setStartDate(start);
                            setEndDate(today);
                            setEnableDateRange(true);
                            setAppliedStartDate(start);
                            setAppliedEndDate(today);
                            setDatePickerAnchorEl(null);
                            resetPagination();
                          }}
                          sx={{ fontWeight: 700, cursor: "pointer" }}
                        />
                      </Stack>

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1 }}>
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => {
                            setStartDate("");
                            setEndDate("");
                            setAppliedStartDate("");
                            setAppliedEndDate("");
                            setEnableDateRange(false);
                            setDatePickerAnchorEl(null);
                            resetPagination();
                          }}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Limpiar
                        </Button>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setDatePickerAnchorEl(null)}
                            sx={{ textTransform: "none", fontWeight: 700, color: "#79849a" }}
                          >
                            Cerrar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={() => {
                              const start = startDate || (endDate ? addWeeks(endDate, -4) : "");
                              const end = endDate || (startDate ? addWeeks(startDate, 4) : "");
                              setStartDate(start);
                              setEndDate(end);
                              setEnableDateRange(!!(start && end));
                              setAppliedStartDate(start);
                              setAppliedEndDate(end);
                              setDatePickerAnchorEl(null);
                              resetPagination();
                            }}
                            sx={{
                              bgcolor: "#000000",
                              color: "#ffffff",
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: 2,
                              px: 2,
                              "&:hover": { bgcolor: "#111c2d" },
                            }}
                          >
                            Aplicar
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </LocalizationProvider>
                </Popover>
              </Grid>

              <Grid item xs={12} md={3} sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: { xs: 1, md: 2 } }}>
                {(isExternalMode || enableDateRange) && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setIsExternalMode(false);
                      setStartDate("");
                      setEndDate("");
                      setAppliedStartDate("");
                      setAppliedEndDate("");
                      setEnableDateRange(false);
                      resetPagination();
                    }}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      fontWeight: 900,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      fontSize: 10,
                      color: "#93000a",
                      "&:hover": { bgcolor: "#ffdad6" },
                    }}
                  >
                    Desactivar Filtro
                  </Button>
                )}
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!startDate || !endDate}
                  onClick={() => {
                    setAppliedStartDate(startDate);
                    setAppliedEndDate(endDate);
                    setEnableDateRange(true);
                    setIsExternalMode(true);
                    resetPagination();
                  }}
                  sx={{
                    bgcolor: "#000000",
                    color: "#ffffff",
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    fontWeight: 900,
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                    fontSize: 11,
                    "&:hover": { bgcolor: "#111c2d" },
                  }}
                >
                  Consultar API Externa
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {showStats && statsData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid rgba(198,198,205,0.15)",
                boxShadow: "0 10px 30px rgba(25, 28, 30, 0.04)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5, mb: 2.5, borderBottom: "1px solid rgba(198,198,205,0.10)", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span className="material-symbols-outlined" style={{ color: "#191c1e" }}>
                    analytics
                  </span>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, letterSpacing: -0.2 }}>
                    Estadísticas Globales (Admin)
                  </Typography>
                </Box>
                <Chip
                  icon={<span className="material-symbols-outlined" style={{ fontSize: 16, color: "#191c1e" }}>calendar_today</span>}
                  label={`Rango: ${appliedStartDate && appliedEndDate ? `${dayjs(appliedStartDate).format("DD/MM/YYYY")} - ${dayjs(appliedEndDate).format("DD/MM/YYYY")}` : dateRangeString}`}
                  size="small"
                  sx={{
                    bgcolor: "#f7f9fb",
                    color: "#191c1e",
                    fontWeight: 800,
                    fontSize: 11,
                    border: "1px solid rgba(198,198,205,0.3)",
                    px: 0.5
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    label="Volumen Global"
                    icon={<span className="material-symbols-outlined">payments</span>}
                    value={formatMoney(statsData.totalCharges, "USD")}
                    sub="Monto total de cargos filtrados"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    label="Comisiones Globales"
                    icon={<span className="material-symbols-outlined">account_balance_wallet</span>}
                    value={formatMoney(statsData.totalCommissions, "USD")}
                    sub="Suma total de comisiones"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    label="Cargo Promedio"
                    icon={<span className="material-symbols-outlined">analytics</span>}
                    value={formatMoney(statsData.averageCharge, "USD")}
                    sub="Promedio por transacción"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    label="Total Transacciones"
                    icon={<span className="material-symbols-outlined">sync</span>}
                    value={String(statsData.count)}
                    sub="Cantidad de transacciones filtradas"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#45464d", mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
                    Transacciones por Estado
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(statsData.byStatus || {}).map(([statusName, count]) => (
                      <Chip
                        key={statusName}
                        label={`${statusName}: ${count}`}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: 11,
                          bgcolor: statusName === "COMPLETED" ? "#fcdeb5" : statusName === "PENDING" ? "#d0e1fb" : "#f1f3f4",
                          color: statusName === "COMPLETED" ? "#574425" : statusName === "PENDING" ? "#54647a" : "#45464d",
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#45464d", mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
                    Transacciones por Rol
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(statsData.byRole || {}).map(([roleName, count]) => (
                      <Chip
                        key={roleName}
                        label={`${roleName}: ${count}`}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: 11,
                          bgcolor: "#e8f0fe",
                          color: "#1a73e8",
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Libro de transacciones a ancho completo */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "#ffffff",
              boxShadow: "0 20px 40px rgba(25, 28, 30, 0.06)",
            }}
          >
            {/* Table toolbar */}
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                borderBottom: "1px solid rgba(198,198,205,0.15)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.3, color: "#191c1e" }}>
                  Libro de Transacciones Detallado
                </Typography>
                <TextField
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      setQuery(searchVal);
                      resetPagination();
                    }
                  }}
                  placeholder="Buscar en el libro..."
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 240 },
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f2f4f6",
                      borderRadius: 999,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                          search
                        </span>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setQuery(searchVal);
                            resetPagination();
                          }}
                          edge="end"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#000000" }}>
                            arrow_forward
                          </span>
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  value={lookupId}
                  onChange={(e) => setLookupId(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const cleanId = (lookupId || "").trim();
                      if (cleanId) {
                        setSelectedTxId(cleanId);
                        setDrawerOpen(true);
                      }
                    }
                  }}
                  placeholder="Buscar ID de Transacción..."
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f2f4f6",
                      borderRadius: 999,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                          find_in_page
                        </span>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const cleanId = (lookupId || "").trim();
                            if (cleanId) {
                              setSelectedTxId(cleanId);
                              setDrawerOpen(true);
                            }
                          }}
                          edge="end"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#000000" }}>
                            arrow_forward
                          </span>
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Chip
                  label={`Total: ${total}`}
                  size="small"
                  sx={{
                    height: 26,
                    bgcolor: "#e6f4ea",
                    color: "#137333",
                    fontWeight: 900,
                    borderRadius: 999,
                    letterSpacing: 0.4,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  sx={{
                    borderRadius: 2,
                    color: "#45464d",
                    "&:hover": { bgcolor: "#f2f4f6" },
                  }}
                  aria-label="download"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                    download
                  </span>
                </IconButton>
                <Menu
                  anchorEl={downloadAnchorEl}
                  open={downloadMenuOpen}
                  onClose={handleDownloadClose}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      minWidth: 160,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.05)"
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleDownloadClose();
                      exportToExcel();
                    }}
                    sx={{ gap: 1.5, py: 1 }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#137333" }}>table_view</span>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Excel (.xlsx)</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleDownloadClose();
                      handlePrint();
                    }}
                    sx={{ gap: 1.5, py: 1 }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#c5221f" }}>picture_as_pdf</span>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>PDF (.pdf)</Typography>
                  </MenuItem>
                </Menu>
                <IconButton
                  size="small"
                  onClick={handlePrint}
                  sx={{
                    borderRadius: 2,
                    color: "#45464d",
                    "&:hover": { bgcolor: "#f2f4f6" },
                  }}
                  aria-label="print"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                    print
                  </span>
                </IconButton>
              </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 640 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "ID de Transacción",
                      "Fecha de Ejecución",
                      "Emisor y Origen",
                      "Receptor",
                      "Abonado",
                      "Saldo Restante",
                      "Monto",
                      "Comisión",
                      "Estado",
                    ].map((h, idx) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: "#f2f4f6",
                          color: "#45464d",
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing: 1.6,
                          textTransform: "uppercase",
                          borderBottom: "1px solid rgba(198,198,205,0.15)",
                          ...(idx >= 6 ? { textAlign: "right" } : null),
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: limit }).map((_, rIdx) => (
                      <TableRow key={`skeleton-row-${rIdx}`}>
                        {Array.from({ length: 9 }).map((_, cIdx) => (
                          <TableCell key={`skeleton-cell-${rIdx}-${cIdx}`}>
                            <Skeleton variant="text" animation="wave" height={24} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 10, textAlign: "center", color: "#93000a" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 36, verticalAlign: "middle" }}>error</span>
                        <Typography sx={{ mt: 1, fontWeight: 900, fontSize: 14 }}>Error al cargar datos</Typography>
                        <Typography sx={{ fontSize: 12, mt: 0.5, color: "#45464d" }}>{error}</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={fetchTransactions}
                          sx={{ mt: 2, color: "#000000", borderColor: "#000000", "&:hover": { borderColor: "#000000", bgcolor: "rgba(0,0,0,0.04)" } }}
                        >
                          Reintentar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 6, textAlign: "center", color: "#45464d" }}>
                        <Typography sx={{ fontWeight: 900 }}>Sin resultados</Typography>
                        <Typography sx={{ fontSize: 12, mt: 0.5 }}>Ajusta los filtros o tu búsqueda.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((t) => (
                      <TableRow
                        key={t.id}
                        hover
                        onClick={() => {
                          setSelectedTxId(t.id);
                          setDrawerOpen(true);
                        }}
                        sx={{
                          "&:hover": { bgcolor: "#e6e8ea" },
                          cursor: "pointer",
                        }}
                      >
                        <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, fontWeight: 700, color: "#000000" }}>
                          {t.id}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 600 }}>
                          {formatDateTime(t.payment_date)}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#191c1e", lineHeight: 1.15 }}>
                            {t.debitor?.complete_name || "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#45464d", opacity: 0.85, mt: 0.4 }}>
                            {maskAlias(t.debitor?.alias)} · {t.debitor?.bank_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#191c1e", lineHeight: 1.15 }}>
                            {t.creditor?.complete_name || "—"}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.4 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#45464d", opacity: 0.85 }}>
                              {maskAlias(t.creditor?.alias)} · {t.creditor?.bank_name}
                            </Typography>
                            {t.creditor?.alias && (() => {
                              const preset = CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === t.creditor.alias.trim().toLowerCase());
                              if (!preset) return null;
                              const styles = {
                                "Directorio": { bg: "#e6f4ea", text: "#137333" },
                                "Boton de Pago": { bg: "#e8f0fe", text: "#1a73e8" },
                                "Punto Yappy": { bg: "#fef7e0", text: "#b06000" }
                              }[preset.type] || { bg: "#f1f3f4", text: "#5f6368" };
                              return (
                                <Chip
                                  label={preset.type}
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 8,
                                    fontWeight: 900,
                                    bgcolor: styles.bg,
                                    color: styles.text,
                                    px: 0.5,
                                    borderRadius: "4px",
                                    textTransform: "uppercase"
                                  }}
                                />
                              );
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>
                          {(() => {
                            const preset = t.creditor?.alias ? CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === t.creditor.alias.trim().toLowerCase()) : null;
                            const isDirectorio = preset ? preset.type === "Directorio" : false;
                            const hasAbonos = (t.abonos && t.abonos.length > 0) || (abonosMap[t.id] && abonosMap[t.id].length > 0);
                            const label = !isDirectorio ? "Abonado" : (hasAbonos ? "Abonado" : "Sin Abonar");
                            const color = (!isDirectorio || hasAbonos) ? "#137333" : "#79849a";
                            return (
                              <span style={{ color }}>
                                {label}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                          {(() => {
                            const preset = t.creditor?.alias ? CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === t.creditor.alias.trim().toLowerCase()) : null;
                            const isDirectorio = preset ? preset.type === "Directorio" : false;
                            let remainingAmount = 0;
                            if (isDirectorio) {
                              const abonosList = t.abonos || abonosMap[t.id] || [];
                              const abonosSum = Math.round(abonosList.reduce((sum, item) => sum + (item.abonoAmount || 0), 0) * 100) / 100;
                              remainingAmount = Math.max(0, Math.round(((t.charge?.amount || 0) - abonosSum) * 100) / 100);
                            }
                            return formatMoney(remainingAmount, t.charge?.currency);
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 900 }}>
                          {formatMoney(t.charge?.amount, t.charge?.currency)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#45464d", fontWeight: 700 }}>
                          {formatMoney(t.fee?.amount, t.fee?.currency)}
                        </TableCell>
                        <TableCell>
                          {currentlyUpdatingTxId === t.id ? (
                            <Skeleton variant="rectangular" width={75} height={22} sx={{ borderRadius: 999 }} />
                          ) : (
                            statusChip(t.status)
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer */}
            <Box
              sx={{
                p: 2.5,
                bgcolor: "#f2f4f6",
                borderTop: "1px solid rgba(198,198,205,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {isExternal ? (
                <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>
                  Mostrando <span style={{ color: "#191c1e", fontWeight: 900 }}>{((page - 1) * limit) + 1} - {((page - 1) * limit) + filtered.length}</span> transacciones
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>
                  Mostrando <span style={{ color: "#191c1e", fontWeight: 900 }}>{total === 0 ? 0 : ((page - 1) * limit) + 1} - {Math.min(page * limit, total)}</span> de{" "}
                  <span style={{ color: "#191c1e", fontWeight: 900 }}>{total}</span> transacciones
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Button
                  size="small"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ minWidth: 36, px: 1, borderRadius: 2, fontWeight: 900, color: "#191c1e" }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
                </Button>

                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let start = 1;
                  let end = totalPages;
                  if (totalPages > maxVisible) {
                    start = Math.max(1, page - 2);
                    end = Math.min(totalPages, page + 2);
                    if (start === 1) {
                      end = maxVisible;
                    } else if (end === totalPages) {
                      start = totalPages - maxVisible + 1;
                    }
                  }
                  for (let i = start; i <= end; i++) {
                    pages.push(i);
                  }
                  return pages.map((p) => (
                    <Button
                      key={p}
                      size="small"
                      variant={page === p ? "contained" : "text"}
                      disableElevation={page === p}
                      onClick={() => setPage(p)}
                      sx={{
                        minWidth: 36,
                        px: 1.5,
                        borderRadius: 2,
                        bgcolor: page === p ? "#000000" : "transparent",
                        color: page === p ? "#ffffff" : "#191c1e",
                        fontWeight: 900,
                        "&:hover": { bgcolor: page === p ? "#111c2d" : "#eceef0" },
                      }}
                    >
                      {p}
                    </Button>
                  ));
                })()}

                <Button
                  size="small"
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  sx={{ minWidth: 36, px: 1, borderRadius: 2, fontWeight: 900, color: "#191c1e" }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Tarjeta de Cifrado de Bóveda */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#111c2d",
              color: "#ffffff",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <span className="material-symbols-outlined" style={{ color: "#fcdeb5", fontVariationSettings: "'FILL' 1", fontSize: 26 }}>
              verified_user
            </span>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Cifrado de Bóveda Activo</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                Los registros de auditoría de extremo a extremo están bloqueados criptográficamente y verificados por el libro mayor central. (demo)
              </Typography>
            </Box>
          </Paper>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              setSelectedTxId(null);
            }}
            PaperProps={{
              sx: {
                width: { xs: "100%", sm: 500 },
                borderLeft: "1px solid rgba(198,198,205,0.15)",
                boxShadow: "-10px 0 40px rgba(0,0,0,0.05)"
              }
            }}
          >
            <Box sx={{ p: 3, height: "100%", bgcolor: "#f7f9fb", overflowY: "auto" }}>
              {/* Drawer Header */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#191c1e" }}>
                  Detalle de Transacción
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {drawerData && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>search_insights</span>}
                      onClick={() => handleOpenSearchModal()}
                      sx={{
                        borderRadius: 3,
                        borderColor: "rgba(0,0,0,0.15)",
                        color: "#191c1e",
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#000000",
                          bgcolor: "rgba(0,0,0,0.04)"
                        }
                      }}
                    >
                      Consultar Asegurado
                    </Button>
                  )}
                  <IconButton
                    onClick={() => {
                      setDrawerOpen(false);
                      setSelectedTxId(null);
                    }}
                    sx={{ bgcolor: "#eceef0", "&:hover": { bgcolor: "#e6e8ea" } }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                  </IconButton>
                </Box>
              </Box>

              {drawerLoading ? (
                <Stack spacing={3}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="60%" height={28} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                    <Skeleton variant="rectangular" height={50} sx={{ mt: 2, borderRadius: 2 }} />
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1.5 }} />
                    <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1.5 }} />
                    <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
                  </Paper>
                </Stack>
              ) : drawerError ? (
                <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 48, color: "#93000a" }}>error</span>
                  <Typography sx={{ mt: 2, fontWeight: 900, color: "#191c1e" }}>
                    No se pudo cargar el detalle
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: 13, color: "#45464d" }}>
                    {drawerError}
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      const currentId = selectedTxId;
                      setSelectedTxId(null);
                      setTimeout(() => setSelectedTxId(currentId), 50);
                    }}
                    sx={{ mt: 3, bgcolor: "#000000", color: "#ffffff", borderRadius: 3, px: 3, py: 1 }}
                  >
                    Reintentar
                  </Button>
                </Box>
              ) : drawerData ? (
                <Stack spacing={3}>
                  {/* Status & Amount Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, fontWeight: 900, color: "#79849a" }}>
                        {drawerData.id}
                      </Typography>
                      {currentlyUpdatingTxId === drawerData.id ? (
                        <Skeleton variant="rectangular" width={75} height={22} sx={{ borderRadius: 999 }} />
                      ) : (
                        statusChip(drawerData.status)
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 32, fontWeight: 900, color: "#191c1e", letterSpacing: -0.8 }}>
                      {formatMoney(drawerData.charge?.amount, drawerData.charge?.currency)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 800, mt: 0.5 }}>
                      Comisión: {formatMoney(drawerData.fee?.amount, drawerData.fee?.currency)}
                    </Typography>

                    {drawerData.description && (
                      <Box sx={{ mt: 2.5, p: 1.5, bgcolor: "#f7f9fb", borderRadius: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 0.8 }}>
                          Descripción
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#191c1e", fontWeight: 600 }}>
                          {drawerData.description}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Abonos hacia Polizas Card */}
                  {(() => {
                    const isDirectorio = drawerData?.creditor?.alias && (() => {
                      const match = CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === drawerData.creditor.alias.trim().toLowerCase());
                      return match ? match.type === "Directorio" : false;
                    })();

                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          bgcolor: "#ffffff",
                          border: "1px solid rgba(198,198,205,0.15)",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 1.2 }}>
                            Abonos hacia Pólizas
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>}
                            onClick={() => {
                              const rawAlias = drawerData?.debitor?.alias ? String(drawerData.debitor.alias).replace(/\+/g, '').trim() : '';
                              handleOpenSearchModal('telefono', rawAlias);
                            }}
                            disabled={remainingTxAmount <= 0 || !isDirectorio || cannotModifyAbonos}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 800,
                              fontSize: 11,
                              color: '#1a73e8',
                              '&.Mui-disabled': { color: '#79849a' }
                            }}
                          >
                            Vincular Póliza
                          </Button>
                        </Box>

                        {cannotModifyAbonos && (
                          <Box sx={{
                            p: 1.8,
                            mb: 2,
                            borderRadius: 2,
                            bgcolor: "#ffebee",
                            border: "1px solid rgba(244,67,54,0.3)",
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start"
                          }}>
                            <span className="material-symbols-outlined" style={{ color: "#c62828", fontSize: 20 }}>
                              lock
                            </span>
                            <Box>
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#c62828" }}>
                                Acceso Restringido
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: "#b71c1c", mt: 0.5, fontWeight: 600 }}>
                                Solo administradores pueden agregar o modificar abonos en transacciones anteriores a Junio de 2026.
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {!isDirectorio && (
                          <Box sx={{
                            p: 1.8,
                            mb: 2,
                            borderRadius: 2,
                            bgcolor: "#fff7e6",
                            border: "1px solid rgba(255,169,64,0.3)",
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start"
                          }}>
                            <span className="material-symbols-outlined" style={{ color: "#d48806", fontSize: 20 }}>
                              warning
                            </span>
                            <Box>
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#874d00" }}>
                                Abonos Deshabilitados
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: "#ad6800", mt: 0.5, fontWeight: 600 }}>
                                Solo se permiten abonos en transacciones con receptor de tipo <strong>Directorio</strong> (ej. segurosgenesis).
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {currentAbonos.length === 0 ? (
                          <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 600, py: 1, textAlign: 'center' }}>
                            No se han aplicado abonos a esta transacción.
                          </Typography>
                        ) : (
                          <Stack spacing={1.5}>
                            {currentAbonos.map((abono) => {
                              const maxEditAllowed = Math.round((remainingTxAmount + abono.abonoAmount) * 100) / 100;
                              const editAmountVal = Number(editingAbonoAmount);
                              const isEditInvalid = !editingAbonoAmount || isNaN(editAmountVal) || editAmountVal <= 0 || editAmountVal > maxEditAllowed;
                              const editErrorText = isEditInvalid ? (editAmountVal > maxEditAllowed ? "Excede límite" : "Inválido") : null;

                              return (
                                <Paper
                                  key={abono.id}
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "#f8f9fa",
                                    border: "1px solid rgba(0,0,0,0.04)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}
                                >
                                  {editingAbonoId === abono.id ? (
                                    <Box sx={{ display: "flex", flex: 1, gap: 1, alignItems: "center" }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#191c1e" }}>
                                          {abono.polizaNo} {abono.vigencia && (
                                            <Box component="span" sx={{ fontWeight: 500, fontSize: 10, color: '#79849a', ml: 1 }}>
                                              ({abono.vigencia})
                                            </Box>
                                          )}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {abono.asegurado}
                                        </Typography>
                                        {(abono.polizaHash || abono.PolizaHast || abono.policeHast) && (
                                          <Typography sx={{ fontSize: 9, color: "#1a73e8", fontWeight: 700, mt: 0.5 }}>
                                            Hash: {abono.polizaHash || abono.PolizaHast || abono.policeHast}
                                          </Typography>
                                        )}
                                      </Box>
                                      <TextField
                                        size="small"
                                        type="number"
                                        label="Editar Monto"
                                        error={isEditInvalid}
                                        helperText={editErrorText}
                                        inputProps={{ step: "0.01", min: "0.01", max: String(maxEditAllowed) }}
                                        value={editingAbonoAmount}
                                        onChange={(e) => setEditingAbonoAmount(e.target.value)}
                                        sx={{
                                          width: 140,
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            bgcolor: "#ffffff"
                                          },
                                          "& .MuiFormHelperText-root": {
                                            fontSize: 9,
                                            mx: 0.5
                                          }
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        disabled={isEditInvalid}
                                        onClick={() => {
                                          setAbonosMap(prev => ({
                                            ...prev,
                                            [selectedTxId]: prev[selectedTxId].map(x => {
                                              if (x.id === abono.id) {
                                                return { ...x, abonoAmount: editAmountVal };
                                              }
                                              return x;
                                            })
                                          }));
                                          setEditingAbonoId(null);
                                          setEditingAbonoAmount("");
                                        }}
                                        sx={{ color: "#137333" }}
                                      >
                                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>check</span>
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setEditingAbonoId(null);
                                          setEditingAbonoAmount("");
                                        }}
                                        sx={{ color: "#ba1a1a" }}
                                      >
                                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <>
                                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#191c1e" }}>
                                          {abono.polizaNo} {abono.vigencia && (
                                            <Box component="span" sx={{ fontWeight: 500, fontSize: 10, color: '#79849a', ml: 1 }}>
                                              ({abono.vigencia})
                                            </Box>
                                          )}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {abono.asegurado} · {abono.compania}
                                        </Typography>
                                        {abono.customDate && (
                                          <Typography sx={{ fontSize: 10, color: "#e65100", fontWeight: 800 }}>
                                            Fecha de Cobro: {abono.customDate}
                                          </Typography>
                                        )}
                                        {(abono.polizaHash || abono.PolizaHast || abono.policeHast) && (
                                          <Typography sx={{ fontSize: 9, color: "#1a73e8", fontWeight: 700, mt: 0.5 }}>
                                            Hash: {abono.polizaHash || abono.PolizaHast || abono.policeHast}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#137333" }}>
                                          {formatMoney(abono.abonoAmount)}
                                        </Typography>

                                        {applyingAbonoId === abono.id ? (
                                          <CircularProgress size={16} sx={{ mx: 1, color: "#1a73e8" }} />
                                        ) : abono.status === 'applied' ? (
                                          <Chip
                                            label="Aplicado"
                                            size="small"
                                            sx={{
                                              bgcolor: '#e6f4ea',
                                              color: '#137333',
                                              fontWeight: 900,
                                              fontSize: 9,
                                              height: 20,
                                              px: 0.5
                                            }}
                                          />
                                        ) : cannotModifyAbonos ? (
                                          <Tooltip title="Requiere acceso de administrador para transacciones anteriores a Junio de 2026" arrow>
                                            <span className="material-symbols-outlined" style={{ color: "#79849a", fontSize: 18, marginRight: 8, cursor: "default" }}>
                                              lock
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Tooltip title="Aplicar abono" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  setAbonoToConfirm(abono);
                                                  setAbonoConfirmOpen(true);
                                                }}
                                                sx={{ color: "#1a73e8", p: 0.5 }}
                                              >
                                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar abono" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  setEditingAbonoId(abono.id);
                                                  setEditingAbonoAmount(String(abono.abonoAmount));
                                                }}
                                                sx={{ color: "#79849a", p: 0.5 }}
                                              >
                                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Vincular / Eliminar" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  setAbonosMap(prev => ({
                                                    ...prev,
                                                    [selectedTxId]: prev[selectedTxId].filter(x => x.id !== abono.id)
                                                  }));
                                                }}
                                                sx={{ color: "#ba1a1a", p: 0.5 }}
                                              >
                                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        )}
                                      </Box>
                                    </>
                                  )}
                                </Paper>
                              );
                            })}

                            <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 700 }}>
                                Total Abonado:
                              </Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#191c1e" }}>
                                {formatMoney(currentAbonosSum)} / {formatMoney(txTotalAmount)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 700 }}>
                                Restante:
                              </Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: remainingTxAmount > 0 ? "#1a73e8" : "#79849a" }}>
                                {formatMoney(remainingTxAmount)}
                              </Typography>
                            </Box>
                          </Stack>
                        )}
                      </Paper>
                    );
                  })()}

                  {/* Debitor & Creditor Info */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 1.2, mb: 2 }}>
                      Información de las Partes
                    </Typography>

                    {/* Debitor */}
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Emisor / Debitor
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#191c1e", mt: 0.2 }}>
                            {drawerData.debitor?.complete_name || "—"}
                          </Typography>
                        </Box>
                        {drawerData.debitor?.complete_name && (
                          <Tooltip title="Buscar por nombre o subgrupo" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSearchModal('subgrupo', drawerData.debitor?.complete_name)}
                              sx={{
                                color: "#45464d",
                                border: "1px solid rgba(198,198,205,0.2)",
                                borderRadius: 2,
                                p: 0.5,
                                "&:hover": { bgcolor: "#f2f4f6", color: "#000000" }
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>search</span>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700, mt: 0.2 }}>
                        Alias: {maskAlias(drawerData.debitor?.alias)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 600, mt: 0.2 }}>
                        {drawerData.debitor?.bank_name}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: "rgba(198,198,205,0.15)" }} />

                    {/* Creditor */}
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Receptor / Creditor
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#191c1e", mt: 0.2 }}>
                            {drawerData.creditor?.complete_name || "—"}
                          </Typography>
                        </Box>
                        {drawerData.creditor?.complete_name && (
                          <Tooltip title="Buscar por nombre o subgrupo" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSearchModal('subgrupo', drawerData.creditor?.complete_name)}
                              sx={{
                                color: "#45464d",
                                border: "1px solid rgba(198,198,205,0.2)",
                                borderRadius: 2,
                                p: 0.5,
                                "&:hover": { bgcolor: "#f2f4f6", color: "#000000" }
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>search</span>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.2 }}>
                        <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>
                          Alias: {maskAlias(drawerData.creditor?.alias)}
                        </Typography>
                        {drawerData.creditor?.alias && (() => {
                          const preset = CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === drawerData.creditor.alias.trim().toLowerCase());
                          if (!preset) return null;
                          const styles = {
                            "Directorio": { bg: "#e6f4ea", text: "#137333" },
                            "Boton de Pago": { bg: "#e8f0fe", text: "#1a73e8" },
                            "Punto Yappy": { bg: "#fef7e0", text: "#b06000" }
                          }[preset.type] || { bg: "#f1f3f4", text: "#5f6368" };
                          return (
                            <Chip
                              label={preset.type}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 9,
                                fontWeight: 800,
                                bgcolor: styles.bg,
                                color: styles.text,
                                px: 0.5,
                                borderRadius: "4px",
                                textTransform: "uppercase"
                              }}
                            />
                          );
                        })()}
                      </Box>
                      <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 600, mt: 0.2 }}>
                        {drawerData.creditor?.bank_name}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Transaction Metadata */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 1.2, mb: 2 }}>
                      Metadata de Transacción
                    </Typography>

                    <Stack spacing={1.8}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Número de Transacción</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>
                          {drawerData.number ? `${drawerData.number.slice(0, 8)}...` : "—"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Tipo</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.type === "TXN-COM" ? "Comercio (TXN-COM)" : drawerData.type === "TXN-ECOM" ? "E-Commerce (TXN-ECOM)" : (drawerData.type || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Rol</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.role === "CREDIT" ? "Crédito" : drawerData.role === "DEBIT" ? "Débito" : (drawerData.role || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Categoría</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.category === "INTRABANK" ? "Intrabancaria" : (drawerData.category || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Pago</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                          {formatDateTime(drawerData.payment_date)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Registro</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                          {formatDateTime(drawerData.registration_date)}
                        </Typography>
                      </Box>

                      {drawerData.cut_off_date && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Corte</Typography>
                          <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                            {drawerData.cut_off_date}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              ) : null}
            </Box>
          </Drawer>
        </Stack>

        {/* Policy / Insured Search Modal */}
        <Dialog
          open={searchModalOpen}
          onClose={handleCloseSearchModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 48px rgba(0,0,0,0.1)",
              bgcolor: "#f8f9fa",
            }
          }}
        >
          <DialogTitle sx={{ p: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#191c1e' }}>
                Consulta Externa de Asegurados y Pólizas
              </Typography>
              <IconButton onClick={handleCloseSearchModal} size="small" sx={{ color: '#79849a' }}>
                <span className="material-symbols-outlined">close</span>
              </IconButton>
            </Box>
            <Tabs
              value={searchTab}
              onChange={(e, val) => {
                setSearchTab(val);
                setSearchResults(null);
                setSearchError(null);
                setCedulaActiveSubTab(0);
                setSelectedAsegurado(null);
                setFacilseguroDetails(null);
                setFacilseguroLoading(false);
                setFacilseguroError(null);
                setDetailPolicy(null);
              }}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTab-root': {
                  fontWeight: 800,
                  fontSize: 13,
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 2,
                }
              }}
            >
              <Tab label="Buscar por Cédula" value="cedula" />
              <Tab label="Buscar por Teléfono" value="telefono" />
              <Tab label="Buscar por Subgrupo" value="subgrupo" />
              <Tab label="Buscar por Nombre" value="nombre" />
            </Tabs>
          </DialogTitle>

          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Search Inputs */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearchExecute();
                  }
                }}
                placeholder={
                  searchTab === "cedula"
                    ? "Ingrese cédula (ej. 1-234-567)"
                    : searchTab === "telefono"
                      ? "Ingrese número de teléfono (ej. 50764800248)"
                      : searchTab === "nombre"
                        ? "Ingrese nombre del asegurado"
                        : "Ingrese nombre de subgrupo"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#ffffff",
                    borderRadius: 2.5,
                    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                    "&:hover fieldset": { borderColor: "rgba(0,0,0,0.24)" },
                    "&.Mui-focused fieldset": { borderColor: "#000000" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: "#79849a" }}>
                        search
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={handleSearchExecute}
                disabled={searchLoading || !searchQuery.trim()}
                sx={{
                  bgcolor: "#000000",
                  color: "#ffffff",
                  borderRadius: 2.5,
                  px: 4,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#191c1e" },
                  "&.Mui-disabled": { bgcolor: "#eceef0", color: "#a5a7ab" }
                }}
              >
                {searchLoading ? <CircularProgress size={20} color="inherit" /> : "Buscar"}
              </Button>
            </Box>

            {/* Search Error */}
            {searchError && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: "#ffdad6",
                  color: "#93000a",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <span className="material-symbols-outlined">error</span>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{searchError}</Typography>
              </Paper>
            )}

            {/* Search Loading State */}
            {searchLoading && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                <CircularProgress size={40} sx={{ color: "#000000" }} />
                <Typography sx={{ fontSize: 13, color: "#79849a", fontWeight: 700 }}>
                  Consultando base de datos de Genesis...
                </Typography>
              </Box>
            )}

            {/* Search Results */}
            {!searchLoading && searchResults && (
              <Box>
                {selectedAsegurado ? (
                  // Selected Asegurado View: Show profile and list of facilseguro policies
                  <Box>
                    <Button
                      startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>}
                      onClick={() => {
                        setSelectedAsegurado(null);
                        setFacilseguroDetails(null);
                        setFacilseguroError(null);
                        setSelectedPolicyForAbono(null);
                        setAbonoInputAmount("");
                      }}
                      sx={{
                        color: "#191c1e",
                        fontWeight: 800,
                        textTransform: "none",
                        mb: 2,
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,0.12)",
                        px: 2,
                        '&:hover': {
                          bgcolor: "rgba(0,0,0,0.04)",
                          borderColor: "#000000"
                        }
                      }}
                    >
                      Volver a Asegurados
                    </Button>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: "#ffffff",
                        border: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.01)",
                        mb: 3
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 28, color: "#000000" }}>
                          account_circle
                        </span>
                        <Box>
                          <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#191c1e" }}>
                            {selectedAsegurado.nombre || "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 700 }}>
                            IdEntidad: {selectedAsegurado.idEntidad}
                          </Typography>
                        </Box>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                            Cédula / RUC
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#191c1e" }}>
                            {selectedAsegurado.identificacion || "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                            Celular / Teléfono
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#191c1e" }}>
                            {[selectedAsegurado.celular, selectedAsegurado.telefono].filter(Boolean).join(" / ") || "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 700, textTransform: "uppercase" }}>
                            Correo Electrónico
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1a73e8" }}>
                            {selectedAsegurado.email ? (
                              <a href={`mailto:${selectedAsegurado.email}`} style={{ color: "inherit", textDecoration: "none" }}>
                                {selectedAsegurado.email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    {facilseguroLoading && (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                        <CircularProgress size={36} sx={{ color: "#000000" }} />
                        <Typography sx={{ fontSize: 13, color: "#79849a", fontWeight: 700 }}>
                          Obteniendo pólizas y cuotas desde FacilSeguro...
                        </Typography>
                      </Box>
                    )}

                    {facilseguroError && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          bgcolor: "#ffdad6",
                          color: "#93000a",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2
                        }}
                      >
                        <span className="material-symbols-outlined">error</span>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{facilseguroError}</Typography>
                      </Paper>
                    )}

                    {!facilseguroLoading && facilseguroDetails && (
                      <Box>
                        {(!facilseguroDetails.Polizas || facilseguroDetails.Polizas.length === 0) ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 36, color: "#79849a" }}>
                              folder_open
                            </span>
                            <Typography sx={{ fontWeight: 800, color: "#191c1e" }}>No se encontraron pólizas</Typography>
                            <Typography sx={{ fontSize: 12, color: "#79849a" }}>
                              Este asegurado no tiene pólizas activas registradas en FacilSeguro.
                            </Typography>
                          </Box>
                        ) : (
                          <Stack spacing={1.5}>
                            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#79849a", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
                              Pólizas del Asegurado ({facilseguroDetails.Polizas.length})
                            </Typography>
                            {facilseguroDetails.Polizas.map((poliza, index) => {
                              const cleanNo = getCleanPoliza(poliza.NroPoliza);
                              const vigenciaNo = getVigenciaPoliza(poliza.NroPoliza);
                              const isSelected = selectedPolicyForAbono?.CodPoliza === poliza.CodPoliza;
                              return (
                                <Paper
                                  key={index}
                                  elevation={0}
                                  onClick={() => {
                                    if (remainingTxAmount <= 0) return;
                                    setSelectedPolicyForAbono(poliza);
                                    const defaultVal = Math.min(Number(poliza.PrimaNeta || 0), remainingTxAmount);
                                    setAbonoInputAmount(String(defaultVal));
                                  }}
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 2.5,
                                    bgcolor: '#ffffff',
                                    border: isSelected ? '2px solid #1a73e8' : '1px solid rgba(0,0,0,0.06)',
                                    cursor: remainingTxAmount > 0 ? 'pointer' : 'default',
                                    opacity: remainingTxAmount <= 0 ? 0.8 : 1,
                                    transition: 'all 0.2s',
                                    mb: 1.5,
                                    '&:hover': remainingTxAmount > 0 ? {
                                      bgcolor: '#f8f9fa',
                                      borderColor: isSelected ? '#1a73e8' : 'rgba(0,0,0,0.12)',
                                    } : undefined
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Póliza
                                      </Typography>
                                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#191c1e' }}>
                                        {cleanNo || "—"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                      {poliza.SaldoPoliza > 0 ? (
                                        <Chip
                                          label={`Saldo Pendiente: ${formatMoney(poliza.SaldoPoliza)}`}
                                          size="small"
                                          sx={{
                                            bgcolor: '#fce8e6',
                                            color: '#c5221f',
                                            fontWeight: 800,
                                            fontSize: 10,
                                          }}
                                        />
                                      ) : (
                                        <Chip
                                          label="Saldada"
                                          size="small"
                                          sx={{
                                            bgcolor: '#e6f4ea',
                                            color: '#137333',
                                            fontWeight: 800,
                                            fontSize: 10,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>

                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Compañía</Typography>
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#191c1e' }}>
                                        {poliza.Compania || "—"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ramo / Subramo</Typography>
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#191c1e' }}>
                                        {poliza.Ramo || "—"} {poliza.SubRamo ? `· ${poliza.SubRamo}` : ''}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Total</Typography>
                                      <Typography sx={{ fontSize: 13, fontWeight: 900, color: '#137333' }}>
                                        {formatMoney(poliza.Prima)}
                                      </Typography>
                                    </Grid>
                                    {vigenciaNo && (
                                      <Grid item xs={12} sm={8}>
                                        <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia</Typography>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#191c1e' }}>
                                          {vigenciaNo}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>

                                  <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.04)' }} />

                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDetailPolicy(poliza);
                                      }}
                                      startIcon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>info</span>}
                                      sx={{
                                        borderColor: 'rgba(0,0,0,0.15)',
                                        color: '#191c1e',
                                        fontWeight: 800,
                                        textTransform: 'none',
                                        fontSize: 11,
                                        borderRadius: 2,
                                        '&:hover': {
                                          borderColor: '#000000',
                                          bgcolor: 'rgba(0,0,0,0.02)'
                                        }
                                      }}
                                    >
                                      Ver Detalles
                                    </Button>
                                    {remainingTxAmount > 0 && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        disableElevation
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedPolicyForAbono(poliza);
                                          const defaultVal = Math.min(Number(poliza.PrimaNeta || 0), remainingTxAmount);
                                          setAbonoInputAmount(String(defaultVal));
                                        }}
                                        sx={{
                                          bgcolor: isSelected ? '#137333' : '#1a73e8',
                                          color: '#ffffff',
                                          fontWeight: 800,
                                          textTransform: 'none',
                                          fontSize: 11,
                                          borderRadius: 2,
                                          '&:hover': {
                                            bgcolor: isSelected ? '#0b3d1b' : '#1557b0'
                                          }
                                        }}
                                      >
                                        {isSelected ? 'Seleccionada' : 'Seleccionar para Abono'}
                                      </Button>
                                    )}
                                  </Box>
                                </Paper>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  // Asegurados list: display list of unique Asegurados found
                  <Box>
                    {(() => {
                      const uniqueAsegurados = getUniqueAsegurados(searchResults);
                      if (uniqueAsegurados.length === 0) {
                        return (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 40, color: "#79849a" }}>
                              search_off
                            </span>
                            <Typography sx={{ fontWeight: 800, color: "#191c1e" }}>No se encontraron asegurados</Typography>
                            <Typography sx={{ fontSize: 12, color: "#79849a" }}>
                              Verifique el criterio de búsqueda e intente nuevamente.
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#79849a", textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}>
                            Asegurados Encontrados ({uniqueAsegurados.length})
                          </Typography>
                          <Stack spacing={1.5}>
                            {uniqueAsegurados.map((asegurado, index) => (
                              <Paper
                                key={index}
                                elevation={0}
                                onClick={() => handleSelectAsegurado(asegurado)}
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2.5,
                                  bgcolor: '#ffffff',
                                  border: '1px solid rgba(0,0,0,0.06)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  '&:hover': {
                                    bgcolor: '#f8f9fa',
                                    borderColor: 'rgba(0,0,0,0.15)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
                                  }
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#191c1e', mb: 1 }}>
                                    {asegurado.nombre || "—"}
                                  </Typography>
                                  <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Cédula / RUC</Typography>
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>
                                        {asegurado.identificacion || "—"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Contacto</Typography>
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>
                                        {[asegurado.celular, asegurado.telefono].filter(Boolean).join(" / ") || "—"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Correo Electrónico</Typography>
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>
                                        {asegurado.email || "—"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                                <span className="material-symbols-outlined" style={{ color: '#79849a', fontSize: 24, marginLeft: 16 }}>
                                  chevron_right
                                </span>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            )}

            {/* Zero/Initial state */}
            {!searchLoading && !searchResults && !searchError && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, color: "#79849a" }}>
                  manage_search
                </span>
                <Typography sx={{ fontWeight: 800, color: "#191c1e" }}>Consulte Asegurados / Pólizas</Typography>
                <Typography sx={{ fontSize: 12, color: "#79849a" }}>
                  Ingrese un criterio de búsqueda arriba para consultar la API de Genesis.
                </Typography>
              </Box>
            )}
          </DialogContent>

          {selectedPolicyForAbono && (
            <Paper
              elevation={3}
              sx={{
                mx: 3,
                mb: 1,
                p: 2.5,
                borderRadius: 2.5,
                bgcolor: '#e8f0fe',
                border: '1px solid rgba(26,115,232,0.2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#1a73e8', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Aplicar Abono a Póliza
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#191c1e', mt: 0.2 }}>
                    {getCleanPoliza(selectedPolicyForAbono.NroPoliza)}
                    {getVigenciaPoliza(selectedPolicyForAbono.NroPoliza) && (
                      <span style={{ fontWeight: 500, fontSize: 12, color: '#79849a', marginLeft: 8 }}>
                        ({getVigenciaPoliza(selectedPolicyForAbono.NroPoliza)})
                      </span>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#45464d', fontWeight: 700 }}>
                    Asegurado: {selectedPolicyForAbono.Asegurado || selectedPolicyForAbono.Asegurado || "—"}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedPolicyForAbono(null);
                    setAbonoInputAmount("");
                  }}
                  sx={{ color: '#79849a' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Monto del Abono ($)"
                  size="small"
                  type="number"
                  error={Boolean(abonoError)}
                  helperText={abonoError}
                  inputProps={{ step: "0.01", min: "0.01", max: String(remainingTxAmount) }}
                  value={abonoInputAmount}
                  onChange={(e) => setAbonoInputAmount(e.target.value)}
                  sx={{
                    width: 170,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#ffffff",
                      borderRadius: 2,
                    }
                  }}
                />
                <TextField
                  label="Fecha de Cobro"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={abonoInputDate}
                  onChange={(e) => setAbonoInputDate(e.target.value)}
                  sx={{
                    width: 160,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#ffffff",
                      borderRadius: 2,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      const maxVal = remainingTxAmount;
                      const defaultVal = Math.min(Number(selectedPolicyForAbono.PrimaNeta || 0), maxVal);
                      setAbonoInputAmount(String(defaultVal));
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#1a73e8' }}
                  >
                    Monto Sugerido
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={Boolean(abonoError) || !abonoInputAmount}
                    onClick={() => {
                      const amount = Number(abonoInputAmount);
                      if (isNaN(amount) || amount <= 0 || amount > remainingTxAmount) return;

                      const cleanNo = getCleanPoliza(selectedPolicyForAbono.NroPoliza);
                      const newAbono = {
                        id: Date.now(),
                        polizaNo: cleanNo,
                        vigencia: getVigenciaPoliza(selectedPolicyForAbono.NroPoliza),
                        asegurado: selectedPolicyForAbono.Asegurado || selectedPolicyForAbono.Asegurado || "—",
                        compania: selectedPolicyForAbono.Compania || selectedPolicyForAbono["Compania seguros"] || "—",
                        abonoAmount: amount,
                        customDate: abonoInputDate,
                        originalPolicy: selectedPolicyForAbono
                      };

                      setAbonosMap(prev => ({
                        ...prev,
                        [selectedTxId]: [...(prev[selectedTxId] || []), newAbono]
                      }));

                      setSelectedPolicyForAbono(null);
                      setAbonoInputAmount("");
                    }}
                    sx={{
                      bgcolor: '#1a73e8',
                      color: '#ffffff',
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#1557b0' }
                    }}
                  >
                    Agregar Abono
                  </Button>
                </Box>
                <Typography sx={{ fontSize: 11, color: '#79849a', fontWeight: 700, ml: 'auto' }}>
                  Máximo disponible: {formatMoney(remainingTxAmount)}
                </Typography>
              </Box>
            </Paper>
          )}

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseSearchModal}
              sx={{
                color: '#000000',
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 2.5,
                px: 3,
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Policy Details Sub-modal */}
        <Dialog
          open={Boolean(detailPolicy)}
          onClose={() => setDetailPolicy(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
              bgcolor: "#ffffff",
            }
          }}
        >
          {detailPolicy && (
            <>
              <DialogTitle sx={{ p: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#79849a', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Detalle Completo de Póliza
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#191c1e', mt: 0.2 }}>
                    {getCleanPoliza(detailPolicy.NroPoliza)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDetailPolicy(null)} size="small" sx={{ color: '#79849a' }}>
                  <span className="material-symbols-outlined">close</span>
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* General Info */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                    Información de Cobertura
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ramo</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.Ramo || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Subramo</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.SubRamo || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Compañía Aseguradora</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.Compania || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Tipo de Pago</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.TipoPago || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia Inicio</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FechaDesde || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vigencia Fin</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FechaHasta || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Forma de Pago</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#191c1e' }}>{detailPolicy.FormaDePago || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Próximo Pago</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#c5221f' }}>{detailPolicy.FechaProximoPago || "No programado"}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Financial Summary */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                    Resumen Financiero
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Neta</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(detailPolicy.PrimaSinImpuesto)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Impuesto ({detailPolicy.PorcentajeImpuesto || 0}%)</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(detailPolicy.Impuesto)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Prima Total</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#137333' }}>{formatMoney(detailPolicy.Prima)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Saldo Pendiente</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: detailPolicy.SaldoPoliza > 0 ? '#c5221f' : '#137333' }}>
                        {formatMoney(detailPolicy.SaldoPoliza)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Executives */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                    Ejecutivos y Cuentas
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ejecutivo de Cobro</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.EjecutivoCobro || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Ejecutivo de Cuenta</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.EjecutivoCta || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Vendedor / Corredor</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.Vendedor || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#79849a', fontWeight: 700 }}>Líder</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#45464d' }}>{detailPolicy.Lider || "—"}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Documents */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                    Documentos Adjuntos
                  </Typography>
                  {(!detailPolicy.Documentos || detailPolicy.Documentos.length === 0) ? (
                    <Typography sx={{ fontSize: 12, color: '#79849a', fontStyle: 'italic' }}>
                      No hay documentos asociados a esta póliza.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {detailPolicy.Documentos.map((doc, idx) => (
                        <Chip
                          key={idx}
                          icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>picture_as_pdf</span>}
                          label={doc.TipoDocumento || "DOCUMENTO"}
                          onClick={() => {
                            if (doc.PathDocumento) window.open(doc.PathDocumento, '_blank');
                          }}
                          clickable
                          sx={{
                            bgcolor: '#f1f3f4',
                            color: '#1a73e8',
                            fontWeight: 800,
                            fontSize: 11,
                            border: '1px solid rgba(0,0,0,0.06)',
                            '&:hover': {
                              bgcolor: '#e8f0fe',
                              borderColor: '#1a73e8'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Installments (Letras) */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#191c1e', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                    Calendario de Pagos (Letras)
                  </Typography>
                  {(!detailPolicy.Letras || detailPolicy.Letras.length === 0) ? (
                    <Typography sx={{ fontSize: 12, color: '#79849a', fontStyle: 'italic' }}>
                      No hay cuotas programadas.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }}>Fecha Letra</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }} align="right">Monto</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#79849a' }} align="center">Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailPolicy.Letras.map((letra, idx) => {
                            const isPaid = String(letra.Estatus || '').toUpperCase() === 'PAGADO';
                            return (
                              <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell sx={{ fontSize: 12, fontWeight: 700, color: '#45464d' }}>{letra.FechaLetra || '—'}</TableCell>
                                <TableCell sx={{ fontSize: 12, fontWeight: 800, color: '#191c1e' }} align="right">{formatMoney(letra.Monto)}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={letra.Estatus || 'PENDIENTE'}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: 9,
                                      fontWeight: 900,
                                      bgcolor: isPaid ? '#e6f4ea' : '#fce8e6',
                                      color: isPaid ? '#137333' : '#c5221f'
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                <Button
                  onClick={() => setDetailPolicy(null)}
                  variant="contained"
                  disableElevation
                  sx={{
                    bgcolor: '#191c1e',
                    color: '#ffffff',
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': { bgcolor: '#000000' }
                  }}
                >
                  Cerrar Detalles
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Confirmation Dialog for applying abono */}
        <Dialog
          open={abonoConfirmOpen}
          onClose={() => {
            if (applyingAbonoId) return;
            setAbonoConfirmOpen(false);
            setAbonoToConfirm(null);
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 400,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 900, fontSize: 16 }}>
            Confirmar Aplicación de Abono
          </DialogTitle>
          <DialogContent>
            {abonoToConfirm && (
              <Typography sx={{ fontSize: 13, color: '#45464d', fontWeight: 600 }}>
                ¿Está seguro que desea aplicar de forma definitiva el abono de{" "}
                <strong style={{ color: '#137333' }}>{formatMoney(abonoToConfirm.abonoAmount)}</strong> a la póliza{" "}
                <strong>
                  {abonoToConfirm.polizaNo}
                  {abonoToConfirm.vigencia ? ` (${abonoToConfirm.vigencia})` : ""}
                </strong>
                {abonoToConfirm.customDate ? (
                  <> con fecha de cobro <strong>{abonoToConfirm.customDate}</strong></>
                ) : ""}? Esta acción no se podrá editar ni deshacer.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              disabled={Boolean(applyingAbonoId)}
              onClick={() => {
                setAbonoConfirmOpen(false);
                setAbonoToConfirm(null);
              }}
              sx={{ color: '#79849a', textTransform: 'none', fontWeight: 800 }}
            >
              Cancelar
            </Button>
            <Button
              disabled={Boolean(applyingAbonoId)}
              onClick={() => {
                if (!abonoToConfirm) return;
                const targetId = abonoToConfirm.id;
                setApplyingAbonoId(targetId);
                setAbonoConfirmOpen(false);

                // Find matching policy from facilseguroDetails
                let poliza = abonoToConfirm.originalPolicy || null;
                if (!poliza && facilseguroDetails && Array.isArray(facilseguroDetails.Polizas)) {
                  poliza = facilseguroDetails.Polizas.find(p => {
                    const cleanP = getCleanPoliza(p.NroPoliza);
                    return cleanP === abonoToConfirm.polizaNo;
                  }) || null;
                }
                const polizaSafe = poliza || {};
                const aseguradoSafe = selectedAsegurado || lastSelectedAsegurado || {};

                // Generate a random 15-character hex string for PolizaHast
                const generatePolizaHash = () => {
                  const chars = '0123456789abcdef';
                  let result = '';
                  for (let i = 0; i < 15; i++) {
                    result += chars[Math.floor(Math.random() * chars.length)];
                  }
                  return result;
                };
                const polizaHash = generatePolizaHash();

                const idEntidad = aseguradoSafe.idEntidad ?? polizaSafe.idEntidad ?? polizaSafe.IdPersona ?? polizaSafe.idPersona ?? polizaSafe.IDENTIDAD ?? polizaSafe.identidad ?? polizaSafe.IdAsegurado ?? 0;
                const emailAsegurado = aseguradoSafe.email ?? "[EMAIL_ADDRESS]";
                const idGrupo = polizaSafe.IdGrupo ?? 0;
                const idDivisa = polizaSafe.IdDivisa ?? 1;
                const amount = abonoToConfirm.abonoAmount;
                const codPoliza = abonoToConfirm.polizaNo;
                const saldoLetrasVencidas = polizaSafe.SaldoPoliza ?? 0;
                const celular = aseguradoSafe.celular || "";

                // Build successUrl (double-encoding EmailAsegurado to match the required format)
                const successUrlBase = "https://www.facilseguro.com/yappyservices/api/pagosbg/post-payment-yoseguro";
                const emailParam = encodeURIComponent(encodeURIComponent(emailAsegurado));
                const successUrl = `${successUrlBase}?idEntidad=${idEntidad}&idGrupo=${idGrupo}&idDivisa=${idDivisa}&estatus=true&policeHast=${polizaHash}&refresh=true&Amount=${amount}&CodPoliza=${codPoliza}&SaldoLetrasVencidas=${saldoLetrasVencidas}&EmailAsegurado=${emailParam}`;

                // Build failUrl
                const failUrl = `https://www.facilseguro.com/yappy/policyview/${idEntidad}/${idGrupo}/${idDivisa}/false/${polizaHash}`;

                // Build URL parameter inside dataRaw
                const urlParam = `https://segenesis.org/index2.html?merchantId=c6b30b2f-5e80-41c4-9d50-0f4085ab9898&total=${amount}&subtotal=${amount}&taxes=0&paymentDate=${Date.now()}&paymentMethod=YAP&transactionType=VEN&orderId=${polizaHash}&successUrl=${encodeURIComponent(successUrl)}&failUrl=${encodeURIComponent(failUrl)}&domain=https%3A%2F%2Fwww.facilseguro.com%2Fyappiservice%2Fapi&platform=desarrollopropionet&tel=&shipping=0&discount=0&sbx=no`;

                // Build dataRaw object matching required API structure
                const dataRaw = [{
                  Ramo: polizaSafe.Ramo || "",
                  CodPoliza: polizaSafe.CodPoliza + "" || "",
                  NroPoliza: abonoToConfirm.polizaNo,
                  Aseguradora: polizaSafe.Compania || polizaSafe.Aseguradora || abonoToConfirm.compania || "",
                  FechaHasta: polizaSafe.FechaHasta || "",
                  SaldoPoliza: polizaSafe.SaldoPoliza || 0,
                  Exigible: polizaSafe.Exigible ?? abonoToConfirm.abonoAmount,
                  MontoPagar: abonoToConfirm.abonoAmount,
                  Asegurado: polizaSafe.Asegurado || abonoToConfirm.asegurado || "",
                  EjecutivoCobro: polizaSafe.EjecutivoCobro || "",
                  Telefono: celular,
                  IdEntidad: idEntidad,
                  IdGrupo: idGrupo,
                  IdDivisa: idDivisa,
                  Url: urlParam,
                  EmailEjecutivoCta: polizaSafe.EmailEjecutivoCta ?? null,
                  EmailVendedor: polizaSafe.EmailVendedor ?? null,
                  EmailEjecutivoCobro: polizaSafe.EmailEjecutivoCobro ?? null,
                  EmailAsegurado: emailAsegurado,
                  TelefonoEjecutivoCta: polizaSafe.TelefonoEjecutivoCta ?? null,
                  TelefonoVendedor: polizaSafe.TelefonoVendedor ?? null,
                  TelefonoEjecutivoCobro: polizaSafe.TelefonoEjecutivoCobro ?? null,
                  PolizaHast: polizaHash
                }];

                const headers = {
                  "Content-Type": "application/json",
                };
                const currentToken = user?.accessToken || DEFAULT_TOKEN;
                headers["x-access-token"] = currentToken;
                headers["Authorization"] = `Bearer ${currentToken}`;

                // Calculate if the transaction/payment is on the same day as today.
                // We prioritize the custom input date if the user entered one, otherwise fall back to transaction date.
                const txDateStr = drawerData?.payment_date || drawerData?.registration_date || drawerData?.createdAt;
                let isSameDay = true;
                let formattedTxDate = "";

                const finalDateStr = abonoToConfirm?.customDate || txDateStr;
                if (finalDateStr) {
                  let targetDate;
                  if (abonoToConfirm?.customDate) {
                    const parts = abonoToConfirm.customDate.split('-');
                    // Note: months are 0-indexed in JS Date
                    targetDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                  } else {
                    targetDate = new Date(txDateStr);
                  }

                  const today = new Date();
                  isSameDay = targetDate.getFullYear() === today.getFullYear() &&
                    targetDate.getMonth() === today.getMonth() &&
                    targetDate.getDate() === today.getDate();

                  if (abonoToConfirm?.customDate) {
                    formattedTxDate = abonoToConfirm.customDate;
                  } else {
                    const yyyy = targetDate.getFullYear();
                    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
                    const dd = String(targetDate.getDate()).padStart(2, '0');
                    formattedTxDate = `${yyyy}-${mm}-${dd}`;
                  }
                }

                // Set DRY_RUN to true to test and view requests in console first without making real API calls.
                // Set DRY_RUN to false to enable real network calls.
                // const DRY_RUN = true; // Defined at file scope

                console.log("=== DRY RUN / PRINT REQUESTS ===");
                console.log("1. Endpoint URL:", `${process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api"}/facilseguro/yappy-payment`);
                console.log("1. Method: POST");
                console.log("1. Headers:", headers);
                console.log("1. Body (dataRaw):", JSON.stringify(dataRaw, null, 2));
                console.log("2. successUrl GET:", successUrl);
                console.log("3. Backend Save URL:", `${process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api"}/yappy/movement/${selectedTxId}/abonos`);
                console.log("3. Backend Save Body:", JSON.stringify({
                  id: abonoToConfirm.id,
                  polizaNo: abonoToConfirm.polizaNo,
                  vigencia: abonoToConfirm.vigencia || '',
                  asegurado: abonoToConfirm.asegurado,
                  compania: abonoToConfirm.compania,
                  abonoAmount: abonoToConfirm.abonoAmount,
                  status: 'applied',
                  polizaHash: polizaHash,
                  PolizaHast: polizaHash,
                  policeHast: polizaHash
                }, null, 2));
                if (isSameDay) {
                  console.log("4. Internal Policy GET:", `https://segenesis.org:5001/api/yappy/processInternalPolicy?idEntidad=${idEntidad}&polizaHast=${polizaHash}&CodPoliza=${codPoliza}&Amount=${amount}&EmailAsegurado=${encodeURIComponent(emailAsegurado)}&SaldoLetrasVencidas=${saldoLetrasVencidas}`);
                } else {
                  console.log("4. Crear Pago Poliza POST (Not Same Day):", {
                    url: "https://facilseguro.com/SF.YoSeguroApi/api/Polizas/crear_pago_polizaII",
                    headers: { "Content-Type": "multipart/form-data" },
                    data: [{
                      IdPoliza: Number(polizaSafe.CodPoliza || polizaSafe.IdPoliza || polizaSafe.idPoliza || 0),
                      IdFormaPago: "10",
                      Monto: amount,
                      Numero: abonoToConfirm.polizaNo,
                      IdBanco: "3",
                      NroTrnsaccion: String(drawerData?.id || selectedTxId || ""),
                      IdDivisa: Number(idDivisa),
                      Email: emailAsegurado,
                      FechaCobro: formattedTxDate
                    }]
                  });
                  console.log("5. Internal Policy GET (Not Same Day - sendOnly=whatsapp):", `https://segenesis.org:5001/api/yappy/processInternalPolicy?idEntidad=${idEntidad}&polizaHast=${polizaHash}&CodPoliza=${codPoliza}&Amount=${amount}&EmailAsegurado=${encodeURIComponent(emailAsegurado)}&SaldoLetrasVencidas=${saldoLetrasVencidas}&sendOnly=whatsapp`);
                }

                const executeRequests = async () => {
                  if (DRY_RUN) {
                    // In dry run, simulate local UI updates
                    setAbonosMap(prev => {
                      const list = prev[selectedTxId] || [];
                      return {
                        ...prev,
                        [selectedTxId]: list.map(item => {
                          if (item.id === targetId) {
                            return { ...item, status: 'applied', polizaHash, PolizaHast: polizaHash, policeHast: polizaHash };
                          }
                          return item;
                        })
                      };
                    });
                    setApplyingAbonoId(null);
                    setAbonoToConfirm(null);
                    return;
                  }

                  try {
                    // Send to backend endpoint /facilseguro/yappy-payment (POST)
                    await axios.post(
                      `${process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api"}/facilseguro/yappy-payment`,
                      dataRaw,
                      { headers }
                    );

                    // Save transaction abonos state in backend (PUT)
                    const response = await axios.put(
                      `${process.env.REACT_APP_BACKEND_API || "https://genesispanama.art:5000/api"}/yappy/movement/${selectedTxId}/abonos`,
                      {
                        id: abonoToConfirm.id,
                        polizaNo: abonoToConfirm.polizaNo,
                        vigencia: abonoToConfirm.vigencia || '',
                        asegurado: abonoToConfirm.asegurado,
                        compania: abonoToConfirm.compania,
                        abonoAmount: abonoToConfirm.abonoAmount,
                        status: 'applied',
                        polizaHash: polizaHash,
                        PolizaHast: polizaHash,
                        policeHast: polizaHash
                      },
                      { headers }
                    );

                    if (isSameDay) {
                      // Call the internal policy endpoint
                      const internalRes = await axios.get(
                        `https://segenesis.org:5001/api/yappy/processInternalPolicy`,
                        {
                          params: {
                            idEntidad: idEntidad,
                            polizaHast: polizaHash,
                            CodPoliza: codPoliza,
                            Amount: amount,
                            EmailAsegurado: emailAsegurado,
                            SaldoLetrasVencidas: saldoLetrasVencidas
                          }
                        }
                      );
                      if (internalRes.status !== 200) {
                        throw new Error(`processInternalPolicy returned status ${internalRes.status}`);
                      }
                    } else {
                      // Call crear_pago_poliza endpoint
                      const formData = new FormData();
                      const payload = [{
                        IdPoliza: Number(polizaSafe.CodPoliza || polizaSafe.IdPoliza || polizaSafe.idPoliza || 0),
                        IdFormaPago: "10",
                        Monto: amount,
                        Numero: abonoToConfirm.polizaNo,
                        IdBanco: "3",
                        NroTrnsaccion: String(drawerData?.id || selectedTxId || ""),
                        IdDivisa: Number(idDivisa),
                        Email: emailAsegurado,
                        FechaCobro: formattedTxDate
                      }];
                      formData.append("data", JSON.stringify(payload));

                      const internalRes = await axios.post(
                        "https://facilseguro.com/SF.YoSeguroApi/api/Polizas/crear_pago_polizaII",
                        formData,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data"
                          }
                        }
                      );
                      if (internalRes.status !== 200 && internalRes.status !== 201) {
                        throw new Error(`crear_pago_poliza returned status ${internalRes.status}`);
                      }

                      // Call processInternalPolicy with sendOnly=whatsapp when is not the same day
                      const internalRes2 = await axios.get(
                        `https://segenesis.org:5001/api/yappy/processInternalPolicy`,
                        {
                          params: {
                            idEntidad: idEntidad,
                            polizaHast: polizaHash,
                            CodPoliza: codPoliza,
                            Amount: amount,
                            EmailAsegurado: emailAsegurado,
                            SaldoLetrasVencidas: saldoLetrasVencidas,
                            sendOnly: "whatsapp"
                          }
                        }
                      );
                      if (internalRes2.status !== 200) {
                        throw new Error(`processInternalPolicy with sendOnly=whatsapp returned status ${internalRes2.status}`);
                      }
                    }

                    // Call the successUrl only if transaction date is today (isSameDay)
                    if (isSameDay) {
                      try {
                        await axios.get(successUrl);
                      } catch (urlErr) {
                        console.error("Error fetching successUrl:", urlErr);
                      }
                    }

                    if (response.data && response.data.success) {
                      setAbonosMap(prev => {
                        const list = prev[selectedTxId] || [];
                        return {
                          ...prev,
                          [selectedTxId]: list.map(item => {
                            if (item.id === targetId) {
                              return { ...item, status: 'applied', polizaHash, PolizaHast: polizaHash, policeHast: polizaHash };
                            }
                            return item;
                          })
                        };
                      });
                      if (response.data.transaction) {
                        setDrawerData(response.data.transaction);
                      }
                    }
                  } catch (error) {
                    console.error("Error applying abono:", error);
                    if (error.response?.status === 401 || error.response?.status === 403) {
                      logout();
                      return;
                    }
                    alert("Error al aplicar abono: " + (error.response?.data?.message || error.message));
                  } finally {
                    setApplyingAbonoId(null);
                    setAbonoToConfirm(null);
                  }
                };

                executeRequests();
              }}
              sx={{
                bgcolor: '#1a73e8',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                '&:hover': { bgcolor: '#1557b0' }
              }}
            >
              Confirmar y Aplicar
            </Button>
          </DialogActions>
        </Dialog>

        <style>
          {`
            @media print {
              body * {
                visibility: hidden !important;
              }
              #printable-yappy-report, #printable-yappy-report * {
                visibility: visible !important;
              }
              #printable-yappy-report {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                display: block !important;
              }
            }
          `}
        </style>

        <Box
          id="printable-yappy-report"
          sx={{
            display: "none",
            "@media print": {
              display: "block !important",
            },
          }}
        >
          <Box sx={{ p: 4, color: "#191c1e", fontFamily: "sans-serif" }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "2px solid #191c1e", pb: 2, mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>Genesis Seguros</Typography>
                <Typography variant="body2" sx={{ color: "#45464d", mt: 0.5, fontWeight: 600 }}>
                  Reporte de Transacciones Yappy
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 11, color: "#79849a" }}>
                  Generado: {new Date().toLocaleString("es-PA")}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#79849a", mt: 0.5 }}>
                  Total Transacciones: {filtered.length}
                </Typography>
              </Box>
            </Box>

            {/* Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Box sx={{ border: "1px solid #c6c6cd", borderRadius: 2, p: 2, bgcolor: "#f7f9fb" }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, color: "#45464d" }}>
                    Volumen Total
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, mt: 0.5 }}>
                    {formatMoney(totals.volume)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ border: "1px solid #c6c6cd", borderRadius: 2, p: 2, bgcolor: "#f7f9fb" }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, color: "#45464d" }}>
                    Comisiones Totales
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, mt: 0.5 }}>
                    {formatMoney(totals.fees)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ border: "1px solid #c6c6cd", borderRadius: 2, p: 2, bgcolor: "#f7f9fb" }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, color: "#45464d" }}>
                    Transacciones
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, mt: 0.5 }}>
                    {filtered.length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Table */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f7f9fb" }}>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d" }}>ID Transacción</th>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d" }}>Fecha</th>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d" }}>Emisor (Debitor)</th>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d" }}>Receptor (Creditor)</th>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d" }}>Estado</th>
                  <th style={{ padding: "10px", fontSize: "10px", fontWeight: 800, borderBottom: "1px solid #eceef0", textTransform: "uppercase", color: "#45464d", textAlign: "right" }}>Monto</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0" }}>{t.id || "—"}</td>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0" }}>{formatDateTime(t.payment_date || t.registration_date || t.createdAt)}</td>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0" }}>
                      <div style={{ fontWeight: 600 }}>{t.debitor?.complete_name || "—"}</div>
                      <div style={{ fontSize: "10px", color: "#79849a" }}>Alias: {t.debitor?.alias || "—"}</div>
                    </td>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0" }}>
                      <div style={{ fontWeight: 600 }}>{t.creditor?.complete_name || "—"}</div>
                      <div style={{ fontSize: "10px", color: "#79849a", marginTop: "2px" }}>
                        <span>Alias: {t.creditor?.alias || "—"}</span>
                        {t.creditor?.alias && (() => {
                          const preset = CREDITOR_PRESETS.find(p => p.alias.toLowerCase() === t.creditor.alias.trim().toLowerCase());
                          if (!preset) return null;
                          const styles = {
                            "Directorio": { bg: "#e6f4ea", text: "#137333" },
                            "Boton de Pago": { bg: "#e8f0fe", text: "#1a73e8" },
                            "Punto Yappy": { bg: "#fef7e0", text: "#b06000" }
                          }[preset.type] || { bg: "#f1f3f4", text: "#5f6368" };
                          return (
                            <span style={{
                              marginLeft: "6px",
                              fontSize: "9px",
                              fontWeight: 800,
                              padding: "1px 5px",
                              borderRadius: "4px",
                              backgroundColor: styles.bg,
                              color: styles.text,
                              textTransform: "uppercase",
                              display: "inline-block"
                            }}>
                              {preset.type}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0" }}>
                      <span style={{
                        fontSize: "10px",
                        fontWeight: 800,
                        padding: "2px 6px",
                        borderRadius: "999px",
                        backgroundColor: t.status === "COMPLETED" ? "#e6f4ea" : t.status === "PENDING" ? "#e8f0fe" : "#fce8e6",
                        color: t.status === "COMPLETED" ? "#137333" : t.status === "PENDING" ? "#1a73e8" : "#c5221f",
                      }}>
                        {t.status === "COMPLETED" ? "Completado" : t.status === "PENDING" ? "Pendiente" : t.status === "FAILED" ? "Fallido" : (t.status || "—")}
                      </span>
                    </td>
                    <td style={{ padding: "10px", fontSize: "11px", borderBottom: "1px solid #eceef0", textAlign: "right", fontWeight: 700 }}>
                      {formatMoney(t.charge?.amount || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>
      {DRY_RUN && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 1.5,
            borderRadius: "99px",
            background: "rgba(25, 28, 30, 0.9)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.25)",
            color: "#ffffff",
            pointerEvents: "auto",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateX(-50%) scale(1.05)",
              background: "rgba(25, 28, 30, 0.95)",
            },
            animation: "pulseShadow 3s infinite ease-in-out",
            "@keyframes pulseShadow": {
              "0%, 100%": {
                boxShadow: "0 10px 40px rgba(234, 88, 12, 0.25)",
              },
              "50%": {
                boxShadow: "0 10px 45px rgba(234, 88, 12, 0.55)",
              },
            },
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#ea580c" }}>
            warning
          </span>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#ffffff" }}>
            Modo Dry Run Activo (Simulación)
          </Typography>
        </Box>
      )}
    </Box>
  );
}

